import { AgentRuleBundle } from "../agent-config-options";
import { renderClaimVerificationSection } from "./claim-verification";
import {
  DEFAULT_RESOLVED_ISSUE_DEFAULTS,
  ResolvedIssueDefaults,
  labelsForPhase,
} from "./issue-defaults";
import { renderIssueTypeAssignmentStep } from "./issue-type";
import { renderOriginStampSection } from "./origin-label";
import { DEFAULT_AGENT_PATHS, ResolvedAgentPaths } from "./paths";
import { PROJECT_CONTEXT_READER_SECTION } from "./project-context";
import {
  REQ_WRITE_ISSUE_SCHEMA_SECTION,
  REQUIREMENTS_TAXONOMY_DISAMBIGUATION_SECTION,
  REQUIREMENTS_TAXONOMY_TABLE_SECTION,
  REQUIREMENTS_TIER_TABLE_SECTION,
} from "./requirements-taxonomy";
import {
  AGENT_MODEL,
  AGENT_RULE_SCOPE,
  AgentProcedure,
  AgentSkill,
  AgentSubAgent,
} from "../types";

/**
 * Verbatim BR (Business Requirement) template shipped to the consuming
 * project under the requirements-writer skill's reference directory.
 */
const templateBr = (paths: ResolvedAgentPaths): string => `---
title: "BR-NNN: [Business Requirement Title]"
---

## Metadata

| Field | Value |
|---|---|
| **ID** | BR-NNN |
| **Status** | Draft |
| **Tier** | [Platform · Industry · Customer Workflow · Consumer Application] |
| **Implementor** | [Consortium Member · Customer · TBD — required for Customer Workflow and Consumer Application tiers. When Consortium Member, link to org profile] |
| **Customer** | [Link to customer org profile if this requirement originated from or applies to a specific customer. Optional for Platform/Industry tiers, expected for Customer Workflow/Consumer Application tiers] |
| **Priority** | [Critical / High / Medium / Low] |
| **Owner** | [Name or role responsible for this requirement] |
| **Created** | YYYY-MM-DD |
| **Last Updated** | YYYY-MM-DD |

## Business Need

A clear statement of the business problem or opportunity this requirement addresses. Focus on the "why" — what pain point exists, what opportunity we're capturing, or what strategic goal this supports.

## Stakeholders

| Stakeholder | Role | Interest |
|---|---|---|
| [Name/Group] | [Their role] | [What they care about regarding this requirement] |

## Success Metrics

Define how we'll know this requirement has been successfully met. Metrics should be specific and measurable.

| Metric | Target | Measurement Method |
|---|---|---|
| [e.g., Onboarding completion rate] | [e.g., > 80% within 7 days] | [e.g., Analytics funnel tracking] |

## Scope

### In Scope

What is explicitly included in this business requirement.

### Out of Scope

What is explicitly excluded — important for preventing scope creep and setting expectations.

## Assumptions

List assumptions that underpin this requirement. Flag any that carry risk if they turn out to be wrong.

- [Assumption 1]
- [Assumption 2]

## Constraints

Business-level constraints that bound this requirement (budget, timeline, regulatory, organizational).

- [Constraint 1]
- [Constraint 2]

## Dependencies

Other business requirements, external factors, or organizational decisions this requirement depends on.

- [Dependency 1]
- [Dependency 2]

## Traceability

- **Decomposes into:** [FR-NNN](../${paths.requirementCategoryDirs.functional}/FR-NNN-slug.md), [FR-NNN](../${paths.requirementCategoryDirs.functional}/FR-NNN-slug.md)
- **Constrained by:** [NFR-NNN](../${paths.requirementCategoryDirs.nonFunctional}/NFR-NNN-slug.md)
- **Related:** [SEC-NNN](../${paths.requirementCategoryDirs.security}/SEC-NNN-slug.md)
- **Depends on (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [platform/industry requirement this depends on]
- **Enables (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [customer-workflow/consumer-app requirement this enables]

## Open Items

### Identified Gaps

[Document any missing functionality, unhandled edge cases, or incomplete aspects. Number each item and assign a priority (P0/P1/P2/P3). Call out interdependencies with other open items. If none, write "None identified."]

1. **[Gap title]** \`[P0-P3]\`
   [Description of what's missing and why it matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Follow-up Questions

[Document anything ambiguous or where the motivation/reasoning cannot be confidently inferred. Number each item and assign a priority. If none, write "None identified."]

1. **[Question title]** \`[P0-P3]\`
   [The question, with context on why the answer matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Contradictions & Inconsistencies

[Document any conflicts with other requirements, code behavior, or documentation. Number each item and assign a priority. If none, write "None identified."]

1. **[Contradiction title]** \`[P0-P3]\`
   [Description of the conflict and what needs to be resolved.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

## Revision History

| Date | Author | Change |
|---|---|---|
| YYYY-MM-DD | [Name] | Initial draft |
`;

/** Verbatim FR (Functional Requirement) template. */
const templateFr = (paths: ResolvedAgentPaths): string => `---
title: "FR-NNN: [Functional Requirement Title]"
---

## Metadata

| Field | Value |
|---|---|
| **ID** | FR-NNN |
| **Status** | Draft |
| **Tier** | [Platform · Industry · Customer Workflow · Consumer Application] |
| **Implementor** | [Consortium Member · Customer · TBD — required for Customer Workflow and Consumer Application tiers. When Consortium Member, link to org profile] |
| **Customer** | [Link to customer org profile if this requirement originated from or applies to a specific customer. Optional for Platform/Industry tiers, expected for Customer Workflow/Consumer Application tiers] |
| **Priority** | [Must Have / Should Have / Could Have / Won't Have] |
| **Owner** | [Name or role] |
| **Created** | YYYY-MM-DD |
| **Last Updated** | YYYY-MM-DD |

## User Story

**As a** [actor/role],
**I want to** [action/capability],
**So that** [business value/outcome].

## Description

A narrative description of this feature or behavior. Include enough context that someone unfamiliar with the project can understand what the system does and why it matters.

## Actors

| Actor | Description |
|---|---|
| [e.g., Registered User] | [Who they are and what access level they have] |

## Preconditions

Conditions that must be true before this functionality can be used.

- [Precondition 1]
- [Precondition 2]

## Main Flow

The primary happy-path sequence of interactions.

1. [Step 1]
2. [Step 2]
3. [Step 3]

## Alternative Flows

### AF-1: [Alternative Flow Name]

**Trigger:** [What causes this alternative path]

1. [Step 1]
2. [Step 2]

### AF-2: [Alternative Flow Name]

**Trigger:** [What causes this alternative path]

1. [Step 1]
2. [Step 2]

## Exception Flows

### EF-1: [Exception Flow Name]

**Trigger:** [What error condition causes this]

1. [Step 1 — how the system responds]
2. [Step 2]

## Acceptance Criteria

Specific, testable conditions that must be met for this requirement to be considered complete.

- [ ] [Criterion 1 — stated as a concrete, verifiable condition]
- [ ] [Criterion 2]
- [ ] [Criterion 3]

## Business Rules

Rules that govern the behavior described in this requirement.

| Rule ID | Description |
|---|---|
| BIZ-01 | [e.g., Email addresses must be unique across the system] |
| BIZ-02 | [e.g., Passwords must be at least 12 characters] |

## UI/UX Considerations

Notes on how this feature should present to the user. Reference UX requirements where applicable.

## Data Requirements

What data is created, read, updated, or deleted by this feature. Reference DR documents where applicable.

## Traceability

- **Implements:** [BR-NNN](../${paths.requirementCategoryDirs.business}/BR-NNN-slug.md)
- **Constrained by:** [NFR-NNN](../${paths.requirementCategoryDirs.nonFunctional}/NFR-NNN-slug.md)
- **Related:** [INT-NNN](../${paths.requirementCategoryDirs.integration}/INT-NNN-slug.md)
- **Depends on (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [platform/industry requirement this depends on]
- **Enables (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [customer-workflow/consumer-app requirement this enables]

## Open Items

### Identified Gaps

[Document any missing functionality, unhandled edge cases, or incomplete aspects. Number each item and assign a priority (P0/P1/P2/P3). Call out interdependencies with other open items. If none, write "None identified."]

1. **[Gap title]** \`[P0-P3]\`
   [Description of what's missing and why it matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Follow-up Questions

[Document anything ambiguous or where the motivation/reasoning cannot be confidently inferred. Number each item and assign a priority. If none, write "None identified."]

1. **[Question title]** \`[P0-P3]\`
   [The question, with context on why the answer matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Contradictions & Inconsistencies

[Document any conflicts with other requirements, code behavior, or documentation. Number each item and assign a priority. If none, write "None identified."]

1. **[Contradiction title]** \`[P0-P3]\`
   [Description of the conflict and what needs to be resolved.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

## Revision History

| Date | Author | Change |
|---|---|---|
| YYYY-MM-DD | [Name] | Initial draft |
`;

/** Verbatim NFR (Non-Functional Requirement) template. */
const templateNfr = (paths: ResolvedAgentPaths): string => `---
title: "NFR-NNN: [Non-Functional Requirement Title]"
---

## Metadata

| Field | Value |
|---|---|
| **ID** | NFR-NNN |
| **Status** | Draft |
| **Tier** | [Platform · Industry · Customer Workflow · Consumer Application] |
| **Implementor** | [Consortium Member · Customer · TBD — required for Customer Workflow and Consumer Application tiers. When Consortium Member, link to org profile] |
| **Customer** | [Link to customer org profile if this requirement originated from or applies to a specific customer. Optional for Platform/Industry tiers, expected for Customer Workflow/Consumer Application tiers] |
| **Quality Attribute** | [Performance / Reliability / Scalability / Maintainability / Portability / Availability] |
| **Priority** | [Critical / High / Medium / Low] |
| **Owner** | [Name or role] |
| **Created** | YYYY-MM-DD |
| **Last Updated** | YYYY-MM-DD |

## Description

What quality attribute this requirement addresses and why it matters for the system.

## Measurable Targets

Non-functional requirements must have numeric, measurable targets — vague statements like "the system should be fast" are not acceptable.

| Metric | Target | Measurement Method | Measurement Frequency |
|---|---|---|---|
| [e.g., API response time (p99)] | [e.g., < 200ms] | [e.g., Datadog APM percentile tracking] | [e.g., Continuous] |
| [e.g., System availability] | [e.g., 99.9% monthly] | [e.g., Uptime monitoring (Pingdom/UptimeRobot)] | [e.g., Monthly] |

## Current Baseline

If this is being applied to an existing system, document the current measured values so improvement can be tracked.

| Metric | Current Value | Date Measured |
|---|---|---|
| [Metric] | [Current value or "Not yet measured"] | [Date] |

## Rationale

Why this target was chosen. What business need or user expectation drives this number? What are the consequences of not meeting it?

## Applicable Scope

Which parts of the system does this requirement apply to? Be specific — "all API endpoints" or "the checkout flow" rather than "the system."

## Approach

High-level approach for meeting this target. Reference ADRs and TRs for implementation details.

## Monitoring & Alerting

How compliance with this requirement will be monitored in production. Reference OPS requirements where applicable.

| Alert Condition | Threshold | Action |
|---|---|---|
| [e.g., p99 latency exceeds target] | [e.g., > 200ms for 5 minutes] | [e.g., Page on-call engineer] |

## Traceability

- **Supports:** [BR-NNN](../${paths.requirementCategoryDirs.business}/BR-NNN-slug.md)
- **Constrains:** [FR-NNN](../${paths.requirementCategoryDirs.functional}/FR-NNN-slug.md)
- **Monitored by:** [OPS-NNN](../${paths.requirementCategoryDirs.operational}/OPS-NNN-slug.md)
- **Depends on (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [platform/industry requirement this depends on]
- **Enables (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [customer-workflow/consumer-app requirement this enables]

## Open Items

### Identified Gaps

[Document any missing functionality, unhandled edge cases, or incomplete aspects. Number each item and assign a priority (P0/P1/P2/P3). Call out interdependencies with other open items. If none, write "None identified."]

1. **[Gap title]** \`[P0-P3]\`
   [Description of what's missing and why it matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Follow-up Questions

[Document anything ambiguous or where the motivation/reasoning cannot be confidently inferred. Number each item and assign a priority. If none, write "None identified."]

1. **[Question title]** \`[P0-P3]\`
   [The question, with context on why the answer matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Contradictions & Inconsistencies

[Document any conflicts with other requirements, code behavior, or documentation. Number each item and assign a priority. If none, write "None identified."]

1. **[Contradiction title]** \`[P0-P3]\`
   [Description of the conflict and what needs to be resolved.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

## Revision History

| Date | Author | Change |
|---|---|---|
| YYYY-MM-DD | [Name] | Initial draft |
`;

/** Verbatim TR (Technical Requirement) template. */
const templateTr = (paths: ResolvedAgentPaths): string => `---
title: "TR-NNN: [Technical Requirement Title]"
---

## Metadata

| Field | Value |
|---|---|
| **ID** | TR-NNN |
| **Status** | Draft |
| **Tier** | [Platform · Industry · Customer Workflow · Consumer Application] |
| **Implementor** | [Consortium Member · Customer · TBD — required for Customer Workflow and Consumer Application tiers. When Consortium Member, link to org profile] |
| **Customer** | [Link to customer org profile if this requirement originated from or applies to a specific customer. Optional for Platform/Industry tiers, expected for Customer Workflow/Consumer Application tiers] |
| **Category** | [Language / Framework / Database / Infrastructure / Library / Tool] |
| **Owner** | [Name or role] |
| **Created** | YYYY-MM-DD |
| **Last Updated** | YYYY-MM-DD |

## Technology Choice

When **Accepted**: Fill in the selected technology.

When **Proposed**: Write "Pending human review. See Recommendation below." and leave Version/License/Vendor as TBD.

| Field | Value |
|---|---|
| **Technology** | [e.g., PostgreSQL] |
| **Version** | [e.g., 16+ (observed: 16-alpine at time of documentation). Version will naturally drift upward through routine maintenance.] |
| **License** | [e.g., PostgreSQL License (permissive, OSI-approved)] |
| **Vendor/Maintainer** | [e.g., PostgreSQL Global Development Group] |

## Alternatives Considered

List every viable technology option, including the recommended one. Each alternative gets the same level of analysis.

### Option 1: [Technology Name]

**Description:** [What this technology is and how it would be used]

**Pros:**
- [Pro 1]
- [Pro 2]

**Cons:**
- [Con 1]
- [Con 2]

**License:** [License type]
**Maturity:** [Established / Growing / Emerging]

### Option 2: [Technology Name]

**Description:** [What this technology is and how it would be used]

**Pros:**
- [Pro 1]

**Cons:**
- [Con 1]

**License:** [License type]
**Maturity:** [Established / Growing / Emerging]

## Recommendation

**Recommended option:** [Name of the recommended technology]

**Why this option:** [A well-reasoned explanation of why this technology best fits the context, constraints, and goals. Address the key trade-offs honestly — explain why the cons are acceptable and why other options were not preferred.]

**Key trade-offs accepted:** [What you're giving up by choosing this option over others]

When the status is \`Proposed\`, this section frames the decision for the human reviewer. When \`Accepted\`, this section documents the reasoning behind the choice.

## Rationale

When **Accepted**: Summarize why this technology was selected. Reference the ADR that records the decision process if one exists.

When **Proposed**: Write "See Recommendation above. Awaiting human decision."

## Usage Context

How and where this technology is used in the system. Be specific about which components or layers depend on it.

## Version Constraints

Any version pinning, upgrade policies, or compatibility requirements. Assume versions will drift upward over time through routine maintenance unless there is a specific reason to pin.

- **Minimum version:** [version — the floor below which compatibility is not guaranteed]
- **Observed version:** [version at time of documentation]
- **Maximum version:** [version or "latest stable" — only specify if there's a known ceiling]
- **Upgrade policy:** [e.g., "Track latest minor releases within 30 days; major version upgrades require ADR"]
- **Version-sensitive notes:** [Any specific features or APIs used that depend on this version floor — helps future developers understand what might break if they don't meet the minimum]

## Configuration Standards

Key configuration decisions, defaults, or standards that apply to this technology across the project.

## Dependencies & Compatibility

Other technologies this choice interacts with, and any known compatibility constraints.

| Dependency | Relationship | Constraint |
|---|---|---|
| [e.g., Node.js runtime] | [Required by] | [e.g., v18 LTS or later] |

## Risks & Mitigations

| Risk | Likelihood | Impact | Mitigation |
|---|---|---|---|
| [e.g., Vendor discontinues support] | [Low/Medium/High] | [Low/Medium/High] | [e.g., Open-source, community-maintained] |

## Traceability

- **Justified by:** [ADR-NNN](../${paths.requirementCategoryDirs.architecturalDecisions}/ADR-NNN-slug.md)
- **Supports:** [FR-NNN](../${paths.requirementCategoryDirs.functional}/FR-NNN-slug.md)
- **Constrained by:** [NFR-NNN](../${paths.requirementCategoryDirs.nonFunctional}/NFR-NNN-slug.md)
- **Depends on (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [platform/industry requirement this depends on]
- **Enables (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [customer-workflow/consumer-app requirement this enables]

## Open Items

### Identified Gaps

[Document any missing functionality, unhandled edge cases, or incomplete aspects. Number each item and assign a priority (P0/P1/P2/P3). Call out interdependencies with other open items. If none, write "None identified."]

1. **[Gap title]** \`[P0-P3]\`
   [Description of what's missing and why it matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Follow-up Questions

[Document anything ambiguous or where the motivation/reasoning cannot be confidently inferred. Number each item and assign a priority. If none, write "None identified."]

1. **[Question title]** \`[P0-P3]\`
   [The question, with context on why the answer matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Contradictions & Inconsistencies

[Document any conflicts with other requirements, code behavior, or documentation. Number each item and assign a priority. If none, write "None identified."]

1. **[Contradiction title]** \`[P0-P3]\`
   [Description of the conflict and what needs to be resolved.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

## Revision History

| Date | Author | Change |
|---|---|---|
| YYYY-MM-DD | [Name] | Initial draft |
`;

/** Verbatim ADR (Architectural Decision Record) template. */
const templateAdr = (paths: ResolvedAgentPaths): string => `---
title: "ADR-NNN: [Decision Title]"
---

## Metadata

| Field | Value |
|---|---|
| **ID** | ADR-NNN |
| **Status** | Draft |
| **Tier** | [Platform · Industry · Customer Workflow · Consumer Application] |
| **Implementor** | [Consortium Member · Customer · TBD — required for Customer Workflow and Consumer Application tiers. When Consortium Member, link to org profile] |
| **Customer** | [Link to customer org profile if this requirement originated from or applies to a specific customer. Optional for Platform/Industry tiers, expected for Customer Workflow/Consumer Application tiers] |
| **Decision Date** | YYYY-MM-DD |
| **Decision Makers** | [Names or roles involved in the decision] |
| **Created** | YYYY-MM-DD |
| **Last Updated** | YYYY-MM-DD |

## Context

What is the issue that we're seeing that motivates this decision or change? What forces are at play — technical constraints, business pressures, team capabilities, timeline?

## Decision

When **Accepted**: State the architectural decision clearly and concisely. Start with "We will..." or "We have decided to..."

When **Proposed**: Write "Pending human review. See Recommendation below." Do not make the decision — present the analysis so a human can decide.

## Alternatives Considered

List every viable option, including the recommended one. Each alternative gets the same level of analysis — do not shortchange options you don't prefer.

### Alternative 1: [Name]

**Description:** [What this alternative entails — enough detail to evaluate it]

**Pros:**
- [Pro 1]
- [Pro 2]

**Cons:**
- [Con 1]
- [Con 2]

### Alternative 2: [Name]

**Description:** [What this alternative entails]

**Pros:**
- [Pro 1]

**Cons:**
- [Con 1]

## Recommendation

**Recommended option:** [Name of the recommended alternative]

**Why this option:** [A well-reasoned explanation of why this alternative best fits the context, constraints, and goals. Address the key trade-offs honestly — explain why the cons of the recommended option are acceptable and why the pros of rejected alternatives don't outweigh their cons in this context.]

**Key trade-offs accepted:** [What you're giving up by choosing this option over others]

When the status is \`Proposed\`, this section frames the decision for the human reviewer. When \`Accepted\`, this section documents the reasoning behind the choice.

## Consequences

### Positive

- [Positive consequence 1]
- [Positive consequence 2]

### Negative

- [Negative consequence 1 — every decision has trade-offs; document them honestly]
- [Negative consequence 2]

### Neutral

- [Neutral observation about what changes as a result of this decision]

## Compliance & Constraints

Any regulatory, security, or organizational constraints that influenced this decision.

## Implementation Notes

High-level guidance for implementing this decision. Detailed implementation goes in TR and FR documents.

## Traceability

- **Supports:** [BR-NNN](../${paths.requirementCategoryDirs.business}/BR-NNN-slug.md)
- **Implemented by:** [TR-NNN](../${paths.requirementCategoryDirs.technical}/TR-NNN-slug.md)
- **Related:** [ADR-NNN](ADR-NNN-slug.md)
- **Depends on (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [platform/industry requirement this depends on]
- **Enables (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [customer-workflow/consumer-app requirement this enables]

## Open Items

### Identified Gaps

[Document any missing functionality, unhandled edge cases, or incomplete aspects. Number each item and assign a priority (P0/P1/P2/P3). Call out interdependencies with other open items. If none, write "None identified."]

1. **[Gap title]** \`[P0-P3]\`
   [Description of what's missing and why it matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Follow-up Questions

[Document anything ambiguous or where the motivation/reasoning cannot be confidently inferred. Number each item and assign a priority. If none, write "None identified."]

1. **[Question title]** \`[P0-P3]\`
   [The question, with context on why the answer matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Contradictions & Inconsistencies

[Document any conflicts with other requirements, code behavior, or documentation. Number each item and assign a priority. If none, write "None identified."]

1. **[Contradiction title]** \`[P0-P3]\`
   [Description of the conflict and what needs to be resolved.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

## Revision History

| Date | Author | Change |
|---|---|---|
| YYYY-MM-DD | [Name] | Initial draft |
`;

/** Verbatim SEC (Security & Compliance) template. */
const templateSec = (paths: ResolvedAgentPaths): string => `---
title: "SEC-NNN: [Security Requirement Title]"
---

## Metadata

| Field | Value |
|---|---|
| **ID** | SEC-NNN |
| **Status** | Draft |
| **Tier** | [Platform · Industry · Customer Workflow · Consumer Application] |
| **Implementor** | [Consortium Member · Customer · TBD — required for Customer Workflow and Consumer Application tiers. When Consortium Member, link to org profile] |
| **Customer** | [Link to customer org profile if this requirement originated from or applies to a specific customer. Optional for Platform/Industry tiers, expected for Customer Workflow/Consumer Application tiers] |
| **Security Domain** | [Authentication / Authorization / Data Protection / Audit / Compliance / Network / Input Validation] |
| **Priority** | [Critical / High / Medium / Low] |
| **Owner** | [Name or role] |
| **Created** | YYYY-MM-DD |
| **Last Updated** | YYYY-MM-DD |

## Description

What security concern this requirement addresses and why it matters.

## Threat Model Reference

What threats does this requirement mitigate? Reference any formal threat modeling artifacts if they exist.

| Threat | STRIDE Category | Severity | This Requirement Mitigates |
|---|---|---|---|
| [e.g., Unauthorized access to tenant data] | [e.g., Elevation of Privilege] | [Critical/High/Medium/Low] | [How] |

## Requirements

### Controls

Specific security controls that must be implemented.

| Control ID | Description | OWASP ASVS Ref | NIST 800-53 Ref |
|---|---|---|---|
| [e.g., SEC-001-C1] | [e.g., All passwords hashed with bcrypt, cost factor ≥ 12] | [e.g., V2.4.1] | [e.g., IA-5(1)] |

### Policies

Organizational or procedural security policies this requirement enforces.

- [Policy 1]
- [Policy 2]

## Applicable Regulations

| Regulation | Requirement | How We Comply |
|---|---|---|
| [e.g., SOC 2 Type II] | [e.g., Access controls and audit logging] | [e.g., RBAC + immutable audit log] |
| [e.g., GDPR] | [e.g., Data encryption at rest] | [e.g., AES-256 via AWS KMS] |

## Validation Approach

How compliance with this security requirement will be verified.

| Method | Frequency | Responsible Party |
|---|---|---|
| [e.g., Automated security scan] | [e.g., Every CI build] | [e.g., DevOps] |
| [e.g., Penetration test] | [e.g., Annually] | [e.g., External vendor] |

## Acceptance Criteria

- [ ] [Specific, verifiable security condition]
- [ ] [e.g., No passwords stored in plaintext anywhere in the system]
- [ ] [e.g., All API endpoints require authentication except explicitly whitelisted public routes]

## Traceability

- **Protects:** [FR-NNN](../${paths.requirementCategoryDirs.functional}/FR-NNN-slug.md)
- **Constrained by:** [NFR-NNN](../${paths.requirementCategoryDirs.nonFunctional}/NFR-NNN-slug.md)
- **Implements:** [BR-NNN](../${paths.requirementCategoryDirs.business}/BR-NNN-slug.md)
- **Related data:** [DR-NNN](../${paths.requirementCategoryDirs.data}/DR-NNN-slug.md)
- **Depends on (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [platform/industry requirement this depends on]
- **Enables (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [customer-workflow/consumer-app requirement this enables]

## Open Items

### Identified Gaps

[Document any missing functionality, unhandled edge cases, or incomplete aspects. Number each item and assign a priority (P0/P1/P2/P3). Call out interdependencies with other open items. If none, write "None identified."]

1. **[Gap title]** \`[P0-P3]\`
   [Description of what's missing and why it matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Follow-up Questions

[Document anything ambiguous or where the motivation/reasoning cannot be confidently inferred. Number each item and assign a priority. If none, write "None identified."]

1. **[Question title]** \`[P0-P3]\`
   [The question, with context on why the answer matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Contradictions & Inconsistencies

[Document any conflicts with other requirements, code behavior, or documentation. Number each item and assign a priority. If none, write "None identified."]

1. **[Contradiction title]** \`[P0-P3]\`
   [Description of the conflict and what needs to be resolved.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

## Revision History

| Date | Author | Change |
|---|---|---|
| YYYY-MM-DD | [Name] | Initial draft |
`;

/** Verbatim DR (Data Requirement) template. */
const templateDr = (paths: ResolvedAgentPaths): string => `---
title: "DR-NNN: [Data Requirement Title]"
---

## Metadata

| Field | Value |
|---|---|
| **ID** | DR-NNN |
| **Status** | Draft |
| **Tier** | [Platform · Industry · Customer Workflow · Consumer Application] |
| **Implementor** | [Consortium Member · Customer · TBD — required for Customer Workflow and Consumer Application tiers. When Consortium Member, link to org profile] |
| **Customer** | [Link to customer org profile if this requirement originated from or applies to a specific customer. Optional for Platform/Industry tiers, expected for Customer Workflow/Consumer Application tiers] |
| **Data Domain** | [e.g., User Data / Transaction Data / Configuration / Audit Logs / Media Assets] |
| **Classification** | [Public / Internal / Confidential / Restricted] |
| **Owner** | [Name or role] |
| **Created** | YYYY-MM-DD |
| **Last Updated** | YYYY-MM-DD |

## Description

What data this requirement governs, why it exists, and its significance to the system.

## Data Model

Describe the key entities, their attributes, and relationships. Include an entity-relationship summary or reference a diagram.

| Entity | Key Attributes | Relationships |
|---|---|---|
| [e.g., User] | [e.g., id, email, name, created_at] | [e.g., Has many Organizations (via membership)] |

## Data Lifecycle

| Phase | Description | Duration |
|---|---|---|
| Creation | [How and when this data is created] | — |
| Active Use | [How this data is used during normal operations] | [e.g., Indefinite while account active] |
| Archival | [If/how data is archived after active use] | [e.g., After 12 months of inactivity] |
| Deletion | [When and how data is permanently removed] | [e.g., 90 days after account closure] |

## Retention Policy

| Data Type | Retention Period | Legal Basis | Deletion Method |
|---|---|---|---|
| [e.g., User profile data] | [e.g., Account lifetime + 90 days] | [e.g., Contractual obligation] | [e.g., Soft delete → hard delete after retention] |
| [e.g., Audit logs] | [e.g., 7 years] | [e.g., SOC 2 compliance] | [e.g., Automated purge] |

## Backup & Recovery

| Parameter | Value |
|---|---|
| **RPO (Recovery Point Objective)** | [e.g., < 1 hour — maximum acceptable data loss] |
| **RTO (Recovery Time Objective)** | [e.g., < 4 hours — maximum acceptable downtime] |
| **Backup Frequency** | [e.g., Continuous WAL archiving + daily snapshots] |
| **Backup Location** | [e.g., Cross-region S3 with encryption] |
| **Recovery Testing** | [e.g., Quarterly restore test] |

## Data Quality Rules

| Rule | Description | Enforcement |
|---|---|---|
| [e.g., Email uniqueness] | [e.g., No two active users may share an email] | [e.g., Database unique constraint] |

## Privacy & Classification

How this data is classified and what handling rules apply based on that classification.

| Classification Level | Handling Rules |
|---|---|
| Public | No restrictions |
| Internal | Access controlled, not shared externally |
| Confidential | Encrypted at rest, access logged, need-to-know basis |
| Restricted | All of Confidential + additional controls per regulation |

## Traceability

- **Supports:** [FR-NNN](../${paths.requirementCategoryDirs.functional}/FR-NNN-slug.md)
- **Protected by:** [SEC-NNN](../${paths.requirementCategoryDirs.security}/SEC-NNN-slug.md)
- **Stored per:** [TR-NNN](../${paths.requirementCategoryDirs.technical}/TR-NNN-slug.md)
- **Depends on (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [platform/industry requirement this depends on]
- **Enables (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [customer-workflow/consumer-app requirement this enables]

## Open Items

### Identified Gaps

[Document any missing functionality, unhandled edge cases, or incomplete aspects. Number each item and assign a priority (P0/P1/P2/P3). Call out interdependencies with other open items. If none, write "None identified."]

1. **[Gap title]** \`[P0-P3]\`
   [Description of what's missing and why it matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Follow-up Questions

[Document anything ambiguous or where the motivation/reasoning cannot be confidently inferred. Number each item and assign a priority. If none, write "None identified."]

1. **[Question title]** \`[P0-P3]\`
   [The question, with context on why the answer matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Contradictions & Inconsistencies

[Document any conflicts with other requirements, code behavior, or documentation. Number each item and assign a priority. If none, write "None identified."]

1. **[Contradiction title]** \`[P0-P3]\`
   [Description of the conflict and what needs to be resolved.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

## Revision History

| Date | Author | Change |
|---|---|---|
| YYYY-MM-DD | [Name] | Initial draft |
`;

/** Verbatim INT (Integration Requirement) template. */
const templateInt = (paths: ResolvedAgentPaths): string => `---
title: "INT-NNN: [Integration Requirement Title]"
---

## Metadata

| Field | Value |
|---|---|
| **ID** | INT-NNN |
| **Status** | Draft |
| **Tier** | [Platform · Industry · Customer Workflow · Consumer Application] |
| **Implementor** | [Consortium Member · Customer · TBD — required for Customer Workflow and Consumer Application tiers. When Consortium Member, link to org profile] |
| **Customer** | [Link to customer org profile if this requirement originated from or applies to a specific customer. Optional for Platform/Industry tiers, expected for Customer Workflow/Consumer Application tiers] |
| **Integration Type** | [REST API / GraphQL / Webhook / Message Queue / File Transfer / SDK] |
| **Direction** | [Inbound / Outbound / Bidirectional] |
| **External System** | [Name of the third-party or internal system] |
| **Owner** | [Name or role] |
| **Created** | YYYY-MM-DD |
| **Last Updated** | YYYY-MM-DD |

## Description

What this integration does, why it's needed, and what business capability it enables.

## External System Overview

| Field | Value |
|---|---|
| **System** | [e.g., Stripe] |
| **Purpose** | [e.g., Payment processing and subscription management] |
| **API Documentation** | [URL to official docs] |
| **API Version** | [e.g., 2023-10-16] |
| **Authentication** | [e.g., API key (secret key in header)] |

## Integration Points

### [Endpoint/Event 1]: [Name]

| Field | Value |
|---|---|
| **Direction** | [Inbound/Outbound] |
| **Protocol** | [e.g., HTTPS REST] |
| **Endpoint** | [e.g., POST /v1/customers] |
| **Authentication** | [e.g., Bearer token] |
| **Rate Limit** | [e.g., 100 requests/second] |

**Request/Payload:**
\`\`\`
[Brief description or schema reference]
\`\`\`

**Response/Output:**
\`\`\`
[Brief description or schema reference]
\`\`\`

## Error Handling

| Error Scenario | Strategy | Retry Policy |
|---|---|---|
| [e.g., 429 Rate Limited] | [e.g., Exponential backoff] | [e.g., 3 retries, max 30s] |
| [e.g., 500 Server Error] | [e.g., Retry with circuit breaker] | [e.g., 3 retries, then circuit open for 60s] |
| [e.g., Timeout] | [e.g., Retry once, then dead letter queue] | [e.g., 30s timeout, 1 retry] |

## Data Mapping

How data maps between our system and the external system.

| Our Field | External Field | Transformation |
|---|---|---|
| [e.g., user.email] | [e.g., customer.email] | [e.g., Direct mapping] |

## SLA & Availability

| Parameter | Our Expectation | External System's Published SLA |
|---|---|---|
| Availability | [e.g., 99.9%] | [e.g., 99.99%] |
| Response Time | [e.g., < 500ms p99] | [e.g., < 200ms typical] |

## Fallback Behavior

What happens when this integration is unavailable. How does the system degrade gracefully?

## Testing Strategy

| Environment | Approach |
|---|---|
| Development | [e.g., Mock server / sandbox environment] |
| Staging | [e.g., Sandbox API with test credentials] |
| Production | [e.g., Live API with monitoring] |

## Traceability

- **Enables:** [FR-NNN](../${paths.requirementCategoryDirs.functional}/FR-NNN-slug.md)
- **Constrained by:** [NFR-NNN](../${paths.requirementCategoryDirs.nonFunctional}/NFR-NNN-slug.md)
- **Secured by:** [SEC-NNN](../${paths.requirementCategoryDirs.security}/SEC-NNN-slug.md)
- **Depends on (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [platform/industry requirement this depends on]
- **Enables (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [customer-workflow/consumer-app requirement this enables]

## Open Items

### Identified Gaps

[Document any missing functionality, unhandled edge cases, or incomplete aspects. Number each item and assign a priority (P0/P1/P2/P3). Call out interdependencies with other open items. If none, write "None identified."]

1. **[Gap title]** \`[P0-P3]\`
   [Description of what's missing and why it matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Follow-up Questions

[Document anything ambiguous or where the motivation/reasoning cannot be confidently inferred. Number each item and assign a priority. If none, write "None identified."]

1. **[Question title]** \`[P0-P3]\`
   [The question, with context on why the answer matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Contradictions & Inconsistencies

[Document any conflicts with other requirements, code behavior, or documentation. Number each item and assign a priority. If none, write "None identified."]

1. **[Contradiction title]** \`[P0-P3]\`
   [Description of the conflict and what needs to be resolved.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

## Revision History

| Date | Author | Change |
|---|---|---|
| YYYY-MM-DD | [Name] | Initial draft |
`;

/** Verbatim OPS (Operational Requirement) template. */
const templateOps = (paths: ResolvedAgentPaths): string => `---
title: "OPS-NNN: [Operational Requirement Title]"
---

## Metadata

| Field | Value |
|---|---|
| **ID** | OPS-NNN |
| **Status** | Draft |
| **Tier** | [Platform · Industry · Customer Workflow · Consumer Application] |
| **Implementor** | [Consortium Member · Customer · TBD — required for Customer Workflow and Consumer Application tiers. When Consortium Member, link to org profile] |
| **Customer** | [Link to customer org profile if this requirement originated from or applies to a specific customer. Optional for Platform/Industry tiers, expected for Customer Workflow/Consumer Application tiers] |
| **Operations Domain** | [Deployment / Monitoring / Alerting / Logging / Incident Response / Backup] |
| **Owner** | [Name or role] |
| **Created** | YYYY-MM-DD |
| **Last Updated** | YYYY-MM-DD |

## Description

What operational concern this requirement addresses and why it matters for system reliability.

## Current State

If this applies to an existing system, document the current operational posture.

## Requirements

### Deployment

| Parameter | Requirement |
|---|---|
| **Strategy** | [e.g., Blue-green / Rolling / Canary] |
| **Frequency** | [e.g., On-demand, multiple times per day] |
| **Rollback** | [e.g., Automated rollback on health check failure within 5 minutes] |
| **Downtime** | [e.g., Zero-downtime required] |

### Monitoring & Observability

| Signal Type | What to Monitor | Tool | Retention |
|---|---|---|---|
| **Metrics** | [e.g., Request rate, error rate, latency p50/p95/p99] | [e.g., Datadog] | [e.g., 15 months] |
| **Logs** | [e.g., Application logs, access logs, audit logs] | [e.g., CloudWatch → S3] | [e.g., 90 days hot, 1 year cold] |
| **Traces** | [e.g., Distributed request traces] | [e.g., Datadog APM] | [e.g., 30 days] |

### Alerting

| Alert | Condition | Severity | Notification Channel | Escalation |
|---|---|---|---|---|
| [e.g., High error rate] | [e.g., 5xx rate > 1% for 5 min] | [e.g., P1 / Critical] | [e.g., PagerDuty → on-call] | [e.g., If unack'd in 15 min → engineering lead] |

### Incident Response

| Severity | Response Time | Resolution Target | Communication |
|---|---|---|---|
| P1 (Critical) | [e.g., 15 minutes] | [e.g., 1 hour] | [e.g., Status page + stakeholder notification] |
| P2 (Major) | [e.g., 30 minutes] | [e.g., 4 hours] | [e.g., Status page if customer-facing] |
| P3 (Minor) | [e.g., Next business day] | [e.g., 5 business days] | [e.g., Internal tracking only] |

## Runbook References

| Scenario | Runbook Location |
|---|---|
| [e.g., Database failover] | [Link or path] |
| [e.g., High memory usage] | [Link or path] |

## Environments

| Environment | Purpose | Infrastructure | Access |
|---|---|---|---|
| Development | [Local development] | [e.g., Docker Compose] | [e.g., All developers] |
| Staging | [Pre-production testing] | [e.g., AWS, scaled down] | [e.g., Engineering team] |
| Production | [Live system] | [e.g., AWS, full scale] | [e.g., Restricted, via bastion] |

## Traceability

- **Monitors:** [NFR-NNN](../${paths.requirementCategoryDirs.nonFunctional}/NFR-NNN-slug.md)
- **Supports:** [BR-NNN](../${paths.requirementCategoryDirs.business}/BR-NNN-slug.md)
- **Uses:** [TR-NNN](../${paths.requirementCategoryDirs.technical}/TR-NNN-slug.md)
- **Depends on (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [platform/industry requirement this depends on]
- **Enables (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [customer-workflow/consumer-app requirement this enables]

## Open Items

### Identified Gaps

[Document any missing functionality, unhandled edge cases, or incomplete aspects. Number each item and assign a priority (P0/P1/P2/P3). Call out interdependencies with other open items. If none, write "None identified."]

1. **[Gap title]** \`[P0-P3]\`
   [Description of what's missing and why it matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Follow-up Questions

[Document anything ambiguous or where the motivation/reasoning cannot be confidently inferred. Number each item and assign a priority. If none, write "None identified."]

1. **[Question title]** \`[P0-P3]\`
   [The question, with context on why the answer matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Contradictions & Inconsistencies

[Document any conflicts with other requirements, code behavior, or documentation. Number each item and assign a priority. If none, write "None identified."]

1. **[Contradiction title]** \`[P0-P3]\`
   [Description of the conflict and what needs to be resolved.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

## Revision History

| Date | Author | Change |
|---|---|---|
| YYYY-MM-DD | [Name] | Initial draft |
`;

/** Verbatim UX (UX Requirement) template. */
const templateUx = (paths: ResolvedAgentPaths): string => `---
title: "UX-NNN: [UX Requirement Title]"
---

## Metadata

| Field | Value |
|---|---|
| **ID** | UX-NNN |
| **Status** | Draft |
| **Tier** | [Platform · Industry · Customer Workflow · Consumer Application] |
| **Implementor** | [Consortium Member · Customer · TBD — required for Customer Workflow and Consumer Application tiers. When Consortium Member, link to org profile] |
| **Customer** | [Link to customer org profile if this requirement originated from or applies to a specific customer. Optional for Platform/Industry tiers, expected for Customer Workflow/Consumer Application tiers] |
| **UX Domain** | [Accessibility / Responsiveness / Internationalization / Interaction Design / Information Architecture] |
| **Priority** | [Critical / High / Medium / Low] |
| **Owner** | [Name or role] |
| **Created** | YYYY-MM-DD |
| **Last Updated** | YYYY-MM-DD |

## Description

What user experience concern this requirement addresses and why it matters.

## Target Users

| User Segment | Characteristics | Key Needs |
|---|---|---|
| [e.g., Power users] | [e.g., Daily usage, keyboard-heavy workflow] | [e.g., Keyboard shortcuts, bulk actions] |

## Accessibility

| Parameter | Requirement |
|---|---|
| **WCAG Level** | [e.g., 2.1 Level AA] |
| **Screen Reader Support** | [e.g., ARIA labels on all interactive elements] |
| **Keyboard Navigation** | [e.g., All functionality accessible via keyboard] |
| **Color Contrast** | [e.g., Minimum 4.5:1 for normal text, 3:1 for large text] |
| **Motion** | [e.g., Respect prefers-reduced-motion; no auto-playing animations] |

## Responsive Design

| Breakpoint | Target Devices | Layout Behavior |
|---|---|---|
| [e.g., < 375px] | [e.g., Small phones] | [e.g., Single column, stacked navigation] |
| [e.g., 375–768px] | [e.g., Phones, small tablets] | [e.g., Single column, hamburger menu] |
| [e.g., 768–1024px] | [e.g., Tablets] | [e.g., Two-column where appropriate] |
| [e.g., > 1024px] | [e.g., Desktop] | [e.g., Full layout with sidebar navigation] |

## Internationalization (i18n)

| Parameter | Requirement |
|---|---|
| **Supported Languages** | [e.g., English (launch), Spanish, French (Phase 2)] |
| **Text Direction** | [e.g., LTR only at launch; RTL support in backlog] |
| **Date/Time Formats** | [e.g., Locale-aware formatting] |
| **Currency** | [e.g., Display in user's locale, store in USD] |

## Interaction Patterns

Key interaction patterns and principles that apply to this requirement.

| Pattern | Standard | Notes |
|---|---|---|
| [e.g., Form validation] | [e.g., Inline validation on blur, summary on submit] | [e.g., Error messages adjacent to field] |
| [e.g., Loading states] | [e.g., Skeleton screens for content, spinners for actions] | [e.g., No blank screens during loads] |

## Design System Reference

Reference to the project's design system, component library, or style guide if one exists.

## Acceptance Criteria

- [ ] [Specific, verifiable UX condition]
- [ ] [e.g., All pages score ≥ 90 on Lighthouse accessibility audit]
- [ ] [e.g., All interactive elements have visible focus indicators]

## Traceability

- **Enhances:** [FR-NNN](../${paths.requirementCategoryDirs.functional}/FR-NNN-slug.md)
- **Constrained by:** [NFR-NNN](../${paths.requirementCategoryDirs.nonFunctional}/NFR-NNN-slug.md)
- **Supports:** [BR-NNN](../${paths.requirementCategoryDirs.business}/BR-NNN-slug.md)
- **Depends on (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [platform/industry requirement this depends on]
- **Enables (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [customer-workflow/consumer-app requirement this enables]

## Open Items

### Identified Gaps

[Document any missing functionality, unhandled edge cases, or incomplete aspects. Number each item and assign a priority (P0/P1/P2/P3). Call out interdependencies with other open items. If none, write "None identified."]

1. **[Gap title]** \`[P0-P3]\`
   [Description of what's missing and why it matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Follow-up Questions

[Document anything ambiguous or where the motivation/reasoning cannot be confidently inferred. Number each item and assign a priority. If none, write "None identified."]

1. **[Question title]** \`[P0-P3]\`
   [The question, with context on why the answer matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Contradictions & Inconsistencies

[Document any conflicts with other requirements, code behavior, or documentation. Number each item and assign a priority. If none, write "None identified."]

1. **[Contradiction title]** \`[P0-P3]\`
   [Description of the conflict and what needs to be resolved.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

## Revision History

| Date | Author | Change |
|---|---|---|
| YYYY-MM-DD | [Name] | Initial draft |
`;

/** Verbatim MT (Multi-Tenancy & Licensing) template. */
const templateMt = (paths: ResolvedAgentPaths): string => `---
title: "MT-NNN: [Multi-Tenancy Requirement Title]"
---

## Metadata

| Field | Value |
|---|---|
| **ID** | MT-NNN |
| **Status** | Draft |
| **Tier** | [Platform · Industry · Customer Workflow · Consumer Application] |
| **Implementor** | [Consortium Member · Customer · TBD — required for Customer Workflow and Consumer Application tiers. When Consortium Member, link to org profile] |
| **Customer** | [Link to customer org profile if this requirement originated from or applies to a specific customer. Optional for Platform/Industry tiers, expected for Customer Workflow/Consumer Application tiers] |
| **MT Domain** | [Tenant Isolation / Feature Gating / Usage Metering / Billing / Provisioning / Licensing] |
| **Priority** | [Critical / High / Medium / Low] |
| **Owner** | [Name or role] |
| **Created** | YYYY-MM-DD |
| **Last Updated** | YYYY-MM-DD |

## Description

What multi-tenancy concern this requirement addresses and why it matters for the SaaS product.

## Tenant Isolation Model

| Layer | Isolation Strategy | Details |
|---|---|---|
| **Database** | [e.g., Row-level / Schema-per-tenant / Database-per-tenant] | [e.g., tenant_id column on all tenant-scoped tables, enforced by RLS] |
| **Application** | [e.g., Shared application, tenant context from auth token] | [e.g., Middleware extracts tenant from JWT, injects into query context] |
| **Infrastructure** | [e.g., Shared infrastructure / Dedicated compute per tenant] | [e.g., Shared at launch, dedicated option for enterprise tier] |
| **Storage** | [e.g., Shared bucket with tenant prefix / Separate buckets] | [e.g., S3 bucket with tenant-id/ prefix, IAM policies per tenant] |

## Feature Gating

| Feature | Free Tier | Pro Tier | Enterprise Tier |
|---|---|---|---|
| [e.g., Number of users] | [e.g., Up to 5] | [e.g., Up to 50] | [e.g., Unlimited] |
| [e.g., API access] | [e.g., No] | [e.g., Yes, 1000 calls/day] | [e.g., Yes, unlimited] |

### Gating Mechanism

How feature gates are implemented and managed (e.g., feature flag service, plan-based checks, entitlement system).

## Usage Metering

| Dimension | Unit | Collection Method | Billing Cycle |
|---|---|---|---|
| [e.g., API calls] | [e.g., Per request] | [e.g., API gateway counter] | [e.g., Monthly] |
| [e.g., Storage] | [e.g., GB-month] | [e.g., Nightly calculation] | [e.g., Monthly] |

## Tenant Provisioning

| Step | Action | Duration | Automated? |
|---|---|---|---|
| 1 | [e.g., Create tenant record] | [e.g., < 1s] | [e.g., Yes] |
| 2 | [e.g., Provision database schema/rows] | [e.g., < 5s] | [e.g., Yes] |
| 3 | [e.g., Configure DNS/subdomain] | [e.g., < 1 min] | [e.g., Yes] |

## Tenant Offboarding

What happens when a tenant leaves — data retention, export, cleanup procedures.

## Noisy Neighbor Prevention

How the system prevents one tenant from degrading the experience of others.

| Strategy | Implementation |
|---|---|
| [e.g., Rate limiting] | [e.g., Per-tenant rate limits at API gateway] |
| [e.g., Resource quotas] | [e.g., Max storage per tenant per tier] |

## Acceptance Criteria

- [ ] [e.g., Tenant A cannot access Tenant B's data under any circumstances]
- [ ] [e.g., Feature gate changes take effect within 60 seconds]
- [ ] [e.g., New tenant provisioning completes in under 30 seconds]

## Traceability

- **Implements:** [BR-NNN](../${paths.requirementCategoryDirs.business}/BR-NNN-slug.md)
- **Secured by:** [SEC-NNN](../${paths.requirementCategoryDirs.security}/SEC-NNN-slug.md)
- **Constrained by:** [NFR-NNN](../${paths.requirementCategoryDirs.nonFunctional}/NFR-NNN-slug.md)
- **Depends on (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [platform/industry requirement this depends on]
- **Enables (cross-tier):** [XX-NNN](../category/XX-NNN-slug.md) — [customer-workflow/consumer-app requirement this enables]

## Open Items

### Identified Gaps

[Document any missing functionality, unhandled edge cases, or incomplete aspects. Number each item and assign a priority (P0/P1/P2/P3). Call out interdependencies with other open items. If none, write "None identified."]

1. **[Gap title]** \`[P0-P3]\`
   [Description of what's missing and why it matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Follow-up Questions

[Document anything ambiguous or where the motivation/reasoning cannot be confidently inferred. Number each item and assign a priority. If none, write "None identified."]

1. **[Question title]** \`[P0-P3]\`
   [The question, with context on why the answer matters.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

### Contradictions & Inconsistencies

[Document any conflicts with other requirements, code behavior, or documentation. Number each item and assign a priority. If none, write "None identified."]

1. **[Contradiction title]** \`[P0-P3]\`
   [Description of the conflict and what needs to be resolved.]
   → [Depends on / Blocks]: [XX-NNN Open Item #N](../category/XX-NNN-slug.md#open-items) (if applicable)

## Revision History

| Date | Author | Change |
|---|---|---|
| YYYY-MM-DD | [Name] | Initial draft |
`;

/** Verbatim category-README template (one per requirement directory). */
const TEMPLATE_CATEGORY_README = `# [Category Name] Requirements

## Purpose

[One-paragraph description of what belongs in this category and the question it answers.]

## What Belongs Here

- [Type of requirement 1]
- [Type of requirement 2]
- [Type of requirement 3]

## What Does NOT Belong Here

| If your requirement looks like... | It probably belongs in... | Why |
|---|---|---|
| [e.g., "The system must respond in < 200ms"] | [e.g., NFR (Non-Functional)] | [e.g., That's a quality/performance target, not a feature description] |

## Template

Use [\`_template-{PREFIX}.md\`](../templates/_template-{PREFIX}.md) for all new documents in this category.

## Current Documents

| ID | Title | Status | Last Updated |
|---|---|---|---|
| [{PREFIX}-001](/{PREFIX}-001-slug.md) | [Title] | [Status] | [Date] |

## Conventions

- File naming: \`{PREFIX}-NNN-descriptive-slug.md\`
- Number sequentially — check the table above for the next available number. Do not embed per-requirement descriptions, a running change-log, or a mutable "next available: X (…)" counter in this Conventions section; the table above is the sole registry, and each document's ID is finalized at write-start.
- Every document must include YAML frontmatter with a \`title\` field
- Every document must include a \`## Traceability\` section
`;

/** Verbatim top-level requirements README template. */
const templateRequirementsReadme = (paths: ResolvedAgentPaths): string => `---
title: "[Project Name] — Requirements Documentation"
---

This directory contains the structured requirements taxonomy for [Project Name]. It covers the full lifecycle from business intent through operational concerns, providing traceability between strategic goals and implementation work.

## Taxonomy Overview

| Category | Prefix | Directory | Purpose |
|---|---|---|---|
| Business Requirements | BR | [\`${paths.requirementCategoryDirs.business}/\`](${paths.requirementCategoryDirs.business}/README.md) | Strategic intent, stakeholders, success metrics |
| Functional Requirements | FR | [\`${paths.requirementCategoryDirs.functional}/\`](${paths.requirementCategoryDirs.functional}/README.md) | User stories, flows, acceptance criteria |
| Non-Functional Requirements | NFR | [\`${paths.requirementCategoryDirs.nonFunctional}/\`](${paths.requirementCategoryDirs.nonFunctional}/README.md) | Performance, scalability, availability targets |
| Technical Requirements | TR | [\`${paths.requirementCategoryDirs.technical}/\`](${paths.requirementCategoryDirs.technical}/README.md) | Technology choices, framework decisions |
| Architectural Decisions | ADR | [\`${paths.requirementCategoryDirs.architecturalDecisions}/\`](${paths.requirementCategoryDirs.architecturalDecisions}/README.md) | Context, decision, consequences (standard ADR format) |
| Security & Compliance | SEC | [\`${paths.requirementCategoryDirs.security}/\`](${paths.requirementCategoryDirs.security}/README.md) | Auth, encryption, audit, regulatory |
| Data Requirements | DR | [\`${paths.requirementCategoryDirs.data}/\`](${paths.requirementCategoryDirs.data}/README.md) | Data models, retention, classification, RPO/RTO |
| Integration Requirements | INT | [\`${paths.requirementCategoryDirs.integration}/\`](${paths.requirementCategoryDirs.integration}/README.md) | APIs, third-party systems, protocols |
| Operational Requirements | OPS | [\`${paths.requirementCategoryDirs.operational}/\`](${paths.requirementCategoryDirs.operational}/README.md) | Deployment, monitoring, alerting, logging |
| UX Requirements | UX | [\`${paths.requirementCategoryDirs.ux}/\`](${paths.requirementCategoryDirs.ux}/README.md) | Accessibility, responsiveness, i18n |
| Multi-Tenancy & Licensing | MT | [\`${paths.requirementCategoryDirs.multiTenancy}/\`](${paths.requirementCategoryDirs.multiTenancy}/README.md) | Tenant isolation, feature gating, billing |

Each directory contains a \`README.md\` with detailed guidance on what belongs in that category (and what doesn't), common disambiguation tips, and project-specific context. When unsure where a requirement belongs, start with the category README.

## Which Category Do I Use?

When capturing or decomposing a requirement, use the question it answers to determine the correct category:

| Question | Category | Example |
|---|---|---|
| **Why** do we need this? What business value does it deliver? | BR | "We need self-service registration to reduce sales-assisted onboarding costs" |
| **What** does the system do? What is the user-visible behavior? | FR | "A user can register with email and password and receive a verification link" |
| **How well** must the system perform? What quality bar must it meet? | NFR | "Registration API must respond within 200ms at p99" |
| **What technology** are we choosing, and why that one? | TR | "PostgreSQL 16+ for the primary datastore" |
| **What structural decision** are we making, and what are the trade-offs? | ADR | "Use row-level tenant isolation rather than schema-per-tenant" |
| **What must we protect**, and what rules govern that protection? | SEC | "All PII must be encrypted at rest; SOC 2 Type II compliance required" |
| **What data** do we store, for how long, and how do we recover it? | DR | "Tenant data retained for 90 days after account closure, RPO < 1 hour" |
| **What external systems** do we connect to, and how? | INT | "Stripe Billing API for subscription management via REST" |
| **How do we deploy, monitor, and respond** to incidents? | OPS | "Blue-green deployments with PagerDuty alerting on error rate > 1%" |
| **How must the interface look, feel, and behave** for users? | UX | "WCAG 2.1 AA compliance; responsive down to 375px width" |
| **How do we isolate tenants, gate features, and meter usage?** | MT | "Logical tenant isolation at row level; API call metering per plan tier" |

### Common Disambiguation

- **SEC vs NFR:** If it's about protecting data, enforcing access control, or meeting a regulation → SEC. If it's about system performance, uptime, or scalability → NFR. A requirement like "99.9% uptime" is an NFR; "audit logs for all admin actions" is SEC.
- **TR vs ADR:** A TR specifies a concrete technology choice ("use PostgreSQL"). An ADR records a structural decision with context, trade-offs, and alternatives considered ("why row-level isolation over schema-per-tenant"). TRs often follow from ADRs.
- **FR vs INT:** If it describes what the user experiences → FR. If it describes how two systems communicate → INT. "User can pay with a credit card" is FR; "Stripe Checkout API integration for payment processing" is INT.
- **DR vs SEC:** If it's about what data exists, how long we keep it, and how we recover it → DR. If it's about who can access it and how it's protected → SEC. These often cross-reference each other.
- **NFR vs OPS:** If it's a measurable target the system must meet → NFR. If it's about the tooling and processes to run the system → OPS. "p99 latency < 200ms" is NFR; "Datadog APM for latency monitoring" is OPS.

## File Naming Convention

\`\`\`
{PREFIX}-{NNN}-{slug}.md
\`\`\`

- **PREFIX** — category abbreviation from the table above
- **NNN** — three-digit sequential number (e.g., \`001\`, \`012\`)
- **slug** — lowercase kebab-case descriptive name

Examples:
- \`FR-001-user-registration.md\`
- \`SEC-001-multi-tenant-auth.md\`
- \`ADR-001-database-selection.md\`

## Status Definitions

All requirements use a universal status lifecycle:

| Status | Meaning |
|---|---|
| \`Draft\` | Initial capture, under discussion |
| \`Proposed\` | Formally proposed, awaiting review |
| \`Accepted\` | Approved and active |
| \`Implemented\` | Fully delivered |
| \`Deprecated\` | No longer applicable |
| \`Superseded\` | Replaced by another requirement (link to successor) |

## Cross-Referencing Guide

Every requirement document should include a \`## Traceability\` section. Use relative markdown links with the requirement ID as link text.

Example:
\`\`\`markdown
## Traceability

- **Implements:** [FR-001](../${paths.requirementCategoryDirs.functional}/FR-001-user-registration.md)
- **Constrained by:** [NFR-001](../${paths.requirementCategoryDirs.nonFunctional}/NFR-001-uptime-sla.md)
- **Related:** [SEC-001](../${paths.requirementCategoryDirs.security}/SEC-001-multi-tenant-auth.md)
\`\`\`

### Link Types

| Link Type | Meaning |
|---|---|
| **Implements** | This requirement realizes a higher-level requirement |
| **Constrained by** | This requirement is bounded by another requirement |
| **Supports** | This requirement contributes to but does not fully realize another |
| **Supersedes** | This requirement replaces a deprecated one |
| **Related** | Informational relationship |

## Connecting Requirements to Code

- **GitHub issues:** Use the Requirements Traceability section of the issue template to reference requirement IDs
- **PR descriptions:** Include a Requirements section with links to relevant requirement documents
- **Commit footers:** Optionally add \`Implements BR-001\` or \`Supports NFR-003\` lines

## Dependency Flow

Requirements relate to each other in a layered hierarchy:

\`\`\`
Business Requirements (BR)
    → justify epics and initiatives

Functional (FR), Integration (INT), Multi-Tenancy (MT)
    → decompose into feature issues

NFRs, Security (SEC), UX
    → appear as acceptance criteria AND as standalone issues

Architectural Decisions (ADR)
    → spawn implementation issues and serve as context

Technical (TR), Data (DR), Operational (OPS)
    → drive infrastructure and platform issues
\`\`\`
`;

/** Verbatim Standards & Frameworks reference shipped with the skill. */
const STANDARDS_AND_FRAMEWORKS = `# Standards & Frameworks Reference

This reference provides detailed guidance on the standards and frameworks that inform each requirement category. Consult this when you're uncertain about how to structure a requirement, what level of detail is appropriate, or how to handle an edge case within a specific category.

## Table of Contents

1. [BR — BABOK Guide v3](#br--babok-guide-v3)
2. [FR — BABOK v3 + IEEE 830](#fr--babok-v3--ieee-830)
3. [NFR — ISO/IEC 25010:2023](#nfr--isoiec-250102023)
4. [ADR — Nygard ADR + MADR](#adr--nygard-adr--madr)
5. [TR — BABOK v3 Transition Requirements](#tr--babok-v3-transition-requirements)
6. [SEC — OWASP ASVS + NIST SP 800-53](#sec--owasp-asvs--nist-sp-800-53)
7. [DR — DAMA-DMBOK](#dr--dama-dmbok)
8. [INT — Enterprise Integration Patterns + OpenAPI](#int--enterprise-integration-patterns--openapi)
9. [OPS — Google SRE Book + ITIL 4](#ops--google-sre-book--itil-4)
10. [UX — WCAG 2.1 + Nielsen Norman Heuristics](#ux--wcag-21--nielsen-norman-heuristics)
11. [MT — Azure Well-Architected Framework](#mt--azure-well-architected-framework)
12. [Cross-Cutting References](#cross-cutting-references)

---

## BR — BABOK Guide v3

**Standard:** [BABOK Guide v3 — Requirements Classification Schema](https://www.iiba.org/business-analysis-certifications/iiba-certifications/ecba-certification/babok-guide-v3/)

**What it is:** The International Institute of Business Analysis (IIBA) defines business requirements as the highest level of the requirements hierarchy — statements of goals, objectives, and outcomes that describe why a change is being undertaken.

**How it informs BR documents:**
- Business requirements sit at the top of the requirements hierarchy. Every other requirement should eventually trace back to one.
- Stakeholder identification follows BABOK's guidance: identify who cares about this requirement, what their role is, and what their interest is.
- Success metrics should be specific and measurable — BABOK emphasizes that business requirements without measurable success criteria are aspirational statements, not requirements.
- Traceability: BABOK's model flows from Business Requirements → Stakeholder Requirements → Solution Requirements (functional + non-functional) → Transition Requirements. Our taxonomy maps to this: BR → FR/NFR → TR.

**When to consult this:** When you're struggling to articulate *why* something is being built (especially when reverse-engineering from code), when defining stakeholders, or when writing success metrics.

---

## FR — BABOK v3 + IEEE 830

**Standards:**
- [BABOK Guide v3 — Solution Requirements](https://www.iiba.org/business-analysis-certifications/iiba-certifications/ecba-certification/babok-guide-v3/)
- [IEEE 830 (SRS) — Recommended Practice for Software Requirements Specifications](https://standards.ieee.org/ieee/830/1222/)

**What they are:** BABOK defines functional requirements as "solution requirements" that describe user-visible behavior. IEEE 830 provides the classic structure for Software Requirements Specifications, including the concept of structured flows.

**How they inform FR documents:**
- The user story format ("As a... I want... so that...") captures the actor, action, and business value in a single sentence. This comes from agile practice but aligns with BABOK's emphasis on stakeholder-centric requirements.
- Structured flows (main flow, alternative flows, exception flows) come from IEEE 830's recommended practices. The main flow is the happy path. Alternative flows are valid variations. Exception flows handle errors.
- MoSCoW prioritization (Must Have / Should Have / Could Have / Won't Have) comes from the DSDM Agile Project Framework and is used for the Priority field in FR documents.
- Acceptance criteria should be specific, testable, and binary (pass/fail). Each criterion describes one verifiable condition.

**When to consult this:** When structuring a complex feature into flows, when deciding how to decompose a large feature into multiple FRs, when writing acceptance criteria, or when prioritizing requirements.

---

## NFR — ISO/IEC 25010:2023

**Standard:** [ISO/IEC 25010:2023 — Systems and Software Quality Requirements and Evaluation](https://www.iso.org/standard/35733.html)

**What it is:** ISO 25010 defines a product quality model with characteristics and sub-characteristics. It's the international standard for thinking about software quality attributes.

**Quality characteristics defined by ISO 25010:**
- **Functional suitability** — Does it do what it's supposed to? (correctness, completeness, appropriateness)
- **Performance efficiency** — Time behavior, resource utilization, capacity
- **Compatibility** — Co-existence, interoperability
- **Usability** — Recognizability, learnability, operability, error protection, aesthetics, accessibility
- **Reliability** — Maturity, availability, fault tolerance, recoverability
- **Security** — Confidentiality, integrity, non-repudiation, accountability, authenticity (note: in our taxonomy, security gets its own SEC category)
- **Maintainability** — Modularity, reusability, analysability, modifiability, testability
- **Portability** — Adaptability, installability, replaceability

**How it informs NFR documents:**
- Every NFR must have a numeric, measurable target. "The system should be fast" is not an NFR. "API response time must be < 200ms at p99" is.
- The Quality Attribute field in the NFR template maps to ISO 25010's characteristics. Use this to categorize what kind of quality the NFR addresses.
- Measurement methods must be specified — how will you know if the target is being met?

**When to consult this:** When you're not sure if something is an NFR (use the quality characteristics as a checklist), when a stated quality target is vague and needs to be made measurable, or when you need to categorize what type of quality attribute an NFR addresses.

---

## ADR — Nygard ADR + MADR

**Standards:**
- [Nygard ADR Format](https://cognitect.com/blog/2011/11/15/documenting-architecture-decisions) — The original lightweight ADR format
- [MADR — Markdown Architectural Decision Records](https://adr.github.io/madr/) — Extended format with structured alternatives

**What they are:** Michael Nygard's original ADR format (Context → Decision → Consequences) established the standard for documenting architecture decisions as lightweight, immutable records. MADR extends this with explicit alternatives and structured consequences.

**How they inform ADR documents:**
- **Context:** What forces are at play? Technical constraints, business pressures, team capabilities, timeline. This is the "why now" of the decision.
- **Decision:** State it clearly: "We will use X." Not "We are considering X."
- **Alternatives considered:** For each alternative, document what it is, its pros and cons, and why it was rejected. This is crucial — future readers need to know not just what was decided, but what was considered and why other options lost.
- **Consequences:** Every decision has positive, negative, and neutral consequences. Documenting negative consequences honestly is essential — it prevents future developers from relitigating decisions without understanding the trade-offs.
- **Immutability:** ADRs are not edited once accepted — they are superseded by new ADRs. This preserves the decision history.

**When to consult this:** When deciding whether something is an ADR vs. a TR (ADR = structural decision with trade-offs; TR = specific technology choice), when structuring the alternatives section, or when documenting consequences honestly (resist the temptation to only list positives).

---

## TR — BABOK v3 Transition Requirements

**Standard:** [BABOK Guide v3 — Transition Requirements](https://www.iiba.org/business-analysis-certifications/iiba-certifications/ecba-certification/babok-guide-v3/)

**What it is:** BABOK defines transition requirements as capabilities the solution must have to facilitate the transition from the current state to the desired state. In our taxonomy, TRs pin down specific technology, framework, and version choices.

**How it informs TR documents:**
- TRs are concrete and specific: "PostgreSQL 16+", "Express 4.x", "Redis 7". Not "a relational database" (that's an ADR-level decision).
- Every TR should be traceable to an ADR that justifies the choice. If no ADR exists, the rationale should be included in the TR itself.
- Version constraints, licensing, and vendor/maintainer information help with long-term planning and risk assessment.
- Remember the version drift principle: cite observed versions but treat them as floors, not pins.

**When to consult this:** When deciding the granularity of a TR (one per technology, not one per library), when linking TRs to their justifying ADRs, or when documenting version constraints.

---

## SEC — OWASP ASVS + NIST SP 800-53

**Standards:**
- [OWASP Application Security Verification Standard (ASVS)](https://owasp.org/www-project-application-security-verification-standard/) — A framework of security requirements organized by domain
- [NIST SP 800-53 Rev 5](https://csrc.nist.gov/publications/detail/sp/800-53/rev-5/final) — Security and Privacy Controls for Information Systems

**What they are:** OWASP ASVS provides a practical, developer-friendly framework for security requirements organized into chapters: authentication, session management, access control, validation, cryptography, error handling, data protection, communications, malicious code, business logic, files, and API security. NIST 800-53 provides a comprehensive control catalog used for compliance frameworks like FedRAMP and SOC 2.

**How they inform SEC documents:**
- OWASP ASVS chapter references help categorize security requirements. For example:
  - V2: Authentication (password policies, multi-factor, credential storage)
  - V3: Session Management (token generation, timeout, revocation)
  - V4: Access Control (RBAC, least privilege, path traversal)
  - V5: Validation, Sanitization, and Encoding
  - V6: Stored Cryptography
  - V7: Error Handling and Logging
  - V8: Data Protection
  - V13: API and Web Service Security
- NIST 800-53 control families map to compliance requirements (e.g., AC = Access Control, AU = Audit and Accountability, IA = Identification and Authentication)
- STRIDE threat categories (Spoofing, Tampering, Repudiation, Information Disclosure, Denial of Service, Elevation of Privilege) help structure threat model references
- Validation approaches should include both automated (CI security scans, SAST, DAST) and manual (penetration testing, code review) methods

**When to consult this:** When categorizing a security requirement, when linking to specific OWASP ASVS or NIST 800-53 controls, when writing threat model references, when distinguishing between SEC and NFR (security protects; NFR measures quality), or when specifying validation approaches.

---

## DR — DAMA-DMBOK

**Standard:** [DAMA-DMBOK — Data Management Body of Knowledge](https://www.dama.org/cpages/body-of-knowledge)

**What it is:** DAMA-DMBOK covers data governance, data quality, data lifecycle management, data architecture, and related disciplines. It provides frameworks for thinking about data classification, retention, and recovery.

**How it informs DR documents:**
- **Data classification levels:** Public → Internal → Confidential → Restricted. Each level has corresponding handling rules (encryption, access logging, need-to-know).
- **Retention policies:** Every data type needs a retention period, legal basis for retention, and deletion method. DAMA emphasizes that retention is not just "how long" but also "why that long" and "what triggers deletion."
- **RPO/RTO framework:**
  - RPO (Recovery Point Objective): Maximum acceptable data loss measured in time. RPO < 1 hour means you can't lose more than 1 hour of data.
  - RTO (Recovery Time Objective): Maximum acceptable downtime. RTO < 4 hours means the system must be back up within 4 hours.
- **Data lifecycle:** Creation → Active Use → Archival → Deletion. Each phase has different storage, access, and protection requirements.

**When to consult this:** When classifying data sensitivity, when defining retention policies (especially the legal basis), when setting RPO/RTO targets, or when documenting the data lifecycle for a new entity.

---

## INT — Enterprise Integration Patterns + OpenAPI

**Standards:**
- [Enterprise Integration Patterns (Hohpe & Woolf)](https://www.enterpriseintegrationpatterns.com/) — Canonical patterns for system integration
- [OpenAPI Specification](https://www.openapis.org/) — Standard for describing REST APIs

**What they are:** Hohpe & Woolf's Enterprise Integration Patterns define the vocabulary for how systems communicate: messaging, routing, transformation, endpoints. OpenAPI provides the standard for documenting REST API contracts.

**How they inform INT documents:**
- **Direction labels:** Inbound (external system calls us), Outbound (we call external system), Bidirectional
- **Error handling strategies from EIP:**
  - Retry with exponential backoff — for transient failures
  - Circuit breaker — stop calling a failing service to prevent cascade failures
  - Dead letter queue (DLQ) — capture messages that can't be processed for later investigation
  - Idempotency — ensure repeated calls don't cause duplicate effects
- **SLA expectations:** Document both what we expect from the external system and what they expect from us. Include availability, response time, and rate limits.
- **API contracts:** Follow OpenAPI conventions for documenting endpoints, request/response schemas, authentication methods, and error codes.

**When to consult this:** When documenting error handling for an integration (use the EIP vocabulary), when specifying fallback behavior, when distinguishing between FR (what the user experiences) and INT (how systems communicate), or when documenting API contracts.

---

## OPS — Google SRE Book + ITIL 4

**Standards:**
- [Google SRE Book — Site Reliability Engineering](https://sre.google/sre-book/table-of-contents/) — Engineering approach to operations
- [ITIL 4](https://www.axelos.com/certifications/itil-service-management) — IT service management framework

**What they are:** The Google SRE book defines principles for running reliable production systems: SLIs, SLOs, error budgets, toil reduction, incident response. ITIL 4 provides the broader service management framework for change management, incident management, and service operations.

**How they inform OPS documents:**
- **SLIs (Service Level Indicators):** The metrics that measure service health. Examples: request latency, error rate, throughput, availability.
- **SLOs (Service Level Objectives):** Targets for SLIs. Example: "99.9% of requests complete within 200ms." SLOs belong in NFR documents, but OPS documents describe how they're monitored.
- **Observability:** The three pillars — metrics, logs, traces. OPS documents should specify all three: what metrics are collected, what's logged, and whether distributed tracing is in place.
- **Incident response:** Severity levels, response times, escalation paths, communication plans. The SRE book emphasizes blameless postmortems and clear runbooks.
- **Deployment strategies:** Blue-green, canary, rolling, feature flags. Each has different rollback characteristics and risk profiles.

**When to consult this:** When defining monitoring and alerting rules, when structuring incident response procedures, when choosing deployment strategies, or when distinguishing between NFR (the target) and OPS (the machinery that monitors and maintains the target).

---

## UX — WCAG 2.1 + Nielsen Norman Heuristics

**Standards:**
- [WCAG 2.1 — Web Content Accessibility Guidelines](https://www.w3.org/WAI/standards-guidelines/wcag/) — W3C accessibility standard
- [Nielsen Norman Group — 10 Usability Heuristics](https://www.nngroup.com/articles/ten-usability-heuristics/) — Foundational usability principles

**What they are:** WCAG 2.1 defines three levels of accessibility conformance (A, AA, AAA) across four principles: Perceivable, Operable, Understandable, Robust (POUR). Nielsen's heuristics provide a framework for evaluating usability beyond accessibility.

**How they inform UX documents:**
- **WCAG conformance levels:**
  - Level A: Minimum accessibility. Removes the most serious barriers.
  - Level AA: The accepted standard for most applications. Addresses the majority of barriers.
  - Level AAA: The highest level. Often impractical for entire sites but useful for specific features.
- **POUR principles:**
  - Perceivable: Can users perceive the content? (text alternatives, captions, contrast)
  - Operable: Can users operate the interface? (keyboard navigation, timing, seizure prevention)
  - Understandable: Can users understand the content and interface? (readability, predictability, error prevention)
  - Robust: Can the content be interpreted by assistive technologies? (valid markup, ARIA)
- **Nielsen's 10 heuristics:**
  1. Visibility of system status
  2. Match between system and the real world
  3. User control and freedom
  4. Consistency and standards
  5. Error prevention
  6. Recognition rather than recall
  7. Flexibility and efficiency of use
  8. Aesthetic and minimalist design
  9. Help users recognize, diagnose, and recover from errors
  10. Help and documentation
- **Responsive design:** Document specific breakpoints and layout behavior changes, not just "it should be responsive."

**When to consult this:** When specifying accessibility requirements (use WCAG success criteria references like "WCAG 2.1 SC 1.4.3 — Contrast Minimum"), when evaluating an interface against usability heuristics, or when defining responsive breakpoints.

---

## MT — Azure Well-Architected Framework

**Standard:** [Azure Well-Architected Framework — Multi-Tenancy](https://learn.microsoft.com/en-us/azure/architecture/guide/multitenant/overview)

**What it is:** Microsoft's comprehensive guidance on building multi-tenant SaaS applications, covering tenant isolation models, feature gating, metering, billing, and provisioning. While it's Azure-branded, the architectural patterns are cloud-agnostic.

**How it informs MT documents:**
- **Isolation models:**
  - Row-level isolation: Single database, tenant_id column, enforced by RLS policies. Cheapest, hardest to guarantee isolation.
  - Schema-per-tenant: Single database, separate schema per tenant. Middle ground.
  - Database-per-tenant: Full isolation. Most expensive, easiest to guarantee.
  - The framework helps evaluate trade-offs between cost, isolation guarantees, and operational complexity.
- **Feature gating:** Mechanisms for enabling/disabling features per tenant or plan tier. Consider: feature flags, entitlement systems, plan-based checks.
- **Metering dimensions:** What to meter (API calls, storage, users, compute), how to collect (real-time counters, batch aggregation), and how to bill (per-unit, tiered, flat-rate).
- **Provisioning:** Tenant onboarding workflow — creating the tenant record, provisioning resources, configuring DNS/routing. Speed matters for self-service.
- **Noisy neighbor prevention:** Rate limiting, resource quotas, fair scheduling — how to prevent one tenant from degrading the experience of others.

**When to consult this:** When choosing a tenant isolation model, when designing feature gating, when defining metering and billing dimensions, when planning tenant provisioning flows, or when addressing noisy-neighbor concerns.

---

## Tier-Specific Standards Guidance

The four architectural tiers (Platform, Industry, Customer Workflow, Consumer Application) influence which standards are most relevant when writing requirements. The tier names below are the bundle defaults — consuming projects may rename or reduce the tier set in their own \`docs/src/content/docs/project-context.md\`.

### Platform Tier
All category-specific standards apply at their full depth. Platform requirements define the core infrastructure that all other tiers depend on. Pay particular attention to:
- **INT** — Enterprise Integration Patterns and OpenAPI for the headless API surface
- **MT** — Azure Well-Architected Framework for tenant isolation (this is inherently platform-tier)
- **SEC** — OWASP ASVS for the authentication/authorization framework
- **NFR** — ISO 25010 quality targets for the shared infrastructure

### Industry Tier
Regulatory and compliance standards dominate. Industry requirements translate external mandates into platform capabilities:
- **SEC** — NIST SP 800-53 controls mapped to industry-specific regulations
- **INT** — Industry-specific interoperability standards
- **DR** — DAMA-DMBOK data classification applied to industry-specific data sensitivity levels

### Customer Workflow Tier
Focus on configurability and business rules. Standards inform what the platform's workflow engine must support:
- **FR** — BABOK/IEEE 830 for capturing the configurable business logic — emphasis on parameterization (what can the tenant change?) vs. fixed behavior
- **BR** — BABOK stakeholder analysis, noting that the stakeholder is the tenant administrator who configures the workflow, not the end user

### Consumer Application Tier
Front-end and UX standards become primary. These requirements describe what consortium members or customers build on top of the platform:
- **UX** — WCAG 2.1 accessibility requirements, Nielsen Norman heuristics for front-end usability
- **FR** — IEEE 830 structured flows for user-facing interactions in the consuming application
- **INT** — OpenAPI specifications for the API contracts the consumer application consumes (trace back to Platform INT requirements)

Note: Consumer Application requirements follow the same rigor as Platform requirements. The tier distinction is architectural, not qualitative.

---

## Cross-Cutting References

These resources inform the overall taxonomy structure, traceability approach, and cross-referencing conventions across all categories:

### BABOK Guide v3
[IIBA BABOK Guide v3](https://www.iiba.org/business-analysis-certifications/iiba-certifications/ecba-certification/babok-guide-v3/)

The requirements classification schema (Business → Stakeholder → Solution → Transition) is the backbone of the taxonomy hierarchy. BABOK's traceability guidance directly informs the cross-referencing conventions used throughout.

### IEEE 29148:2018
[IEEE 29148:2018 — Systems and Software Engineering — Life Cycle Processes — Requirements Engineering](https://standards.ieee.org/ieee/29148/6937/)

The international standard for requirements engineering processes, covering elicitation, analysis, specification, and validation. Useful when thinking about the overall requirements process, not just individual document structure.

### arc42
[arc42 — Software Architecture Documentation](https://arc42.org/overview)

A practical template for software architecture documentation. Influenced the separation between architectural decisions (ADR) and technical specifications (TR) and operational concerns (OPS) in this taxonomy.

### C4 Model
[C4 Model — Software Architecture Visualization](https://c4model.com/)

Simon Brown's approach to visualizing software architecture at four levels of abstraction: Context, Container, Component, Code. Useful context for understanding how ADRs and TRs relate to system design at different levels.
`;

/**
 * Name of the user-invocable slash command that ships this bundle's
 * reference files. Projen synthesizes skill reference files under
 * `.claude/skills/<SKILL_NAME>/_references/`, so every path the
 * reviewer bundle reads derives from this single string.
 */
const WRITE_REQUIREMENT_SKILL_NAME = "write-requirement";

/**
 * Root directory (relative to the project root) where the
 * `write-requirement` skill synthesizes its reference files.
 */
const WRITE_REQUIREMENT_REFERENCES_ROOT = `.claude/skills/${WRITE_REQUIREMENT_SKILL_NAME}/_references`;

/**
 * Canonical paths to the reference files shipped by the
 * `requirements-writer` bundle. Consumed by the `requirements-reviewer`
 * bundle via `import { REQUIREMENTS_WRITER_PATHS }` so the reviewer's
 * prompt strings cannot drift from the actual synthesized locations.
 *
 * Values are derived from `WRITE_REQUIREMENT_SKILL_NAME` rather than
 * hard-coded: if the skill is ever renamed, both the skill definition
 * and every consumer that reads these constants move together.
 */
export const REQUIREMENTS_WRITER_PATHS = {
  /** Directory containing the 13 category templates (trailing slash). */
  templatesRoot: `${WRITE_REQUIREMENT_REFERENCES_ROOT}/templates/`,
  /** The standards-and-frameworks reference document. */
  standardsRef: `${WRITE_REQUIREMENT_REFERENCES_ROOT}/standards-and-frameworks.md`,
} as const;

/**
 * Regression eval suite shipped alongside the `write-requirement`
 * skill at `.claude/skills/write-requirement/evals/evals.json`. Each
 * entry exercises one branch of the decision-authority rules: pure
 * direct-write (BR/FR), partial-deferral with a Proposed ADR, and a
 * mixed prompt that must route an NFR and an OPS document correctly
 * while deferring tooling selection.
 *
 * Suites follow the skill-evals schema — see the `skill-evals` rule
 * content rendered by `./skill-evals.ts#renderSkillEvalsRuleContent`.
 */
const WRITE_REQUIREMENT_EVALS_JSON = JSON.stringify(
  {
    skill_name: "write-requirement",
    evals: [
      {
        id: 1,
        prompt:
          "We had a kickoff meeting where the team decided we need self-service event registration for B2B conferences. The main pain point is that current solutions take months to configure — operators send spreadsheets and wait weeks for changes. We want organizers to set up registration themselves. Target market is small to mid-size B2B conferences with 500-5000 registrants. Write the BR and FR requirements for this.",
        expected_output:
          "A BR document in the configured business-requirements directory capturing the business need (self-service registration, reduced configuration time, target market) with stakeholders, success metrics, and scope. One or more FR documents in the functional-requirements directory describing the self-service configuration workflow with user stories, main/alternative/exception flows, and acceptance criteria. All documents follow the category template exactly, carry YAML frontmatter, ship as status Draft, and include traceability links between the BR and FRs. No technology choices are made — any tech implications are deferred to Proposed ADR or TR documents.",
        files: [],
        product_context_refs: [
          "Mission",
          "In-Scope Capabilities",
          "Out of Scope",
        ],
      },
      {
        id: 2,
        prompt:
          "We need to add payment processing to our platform. Registrants should be able to pay with credit cards and we need to support multiple registration tiers with different prices. Write the requirements for this.",
        expected_output:
          "FR documents covering payment processing (user-visible behavior: selecting a tier, entering payment, receiving confirmation) and registration tiers (configuring tiers, pricing, capacity). The FR documents do NOT select a specific payment provider — instead, a Proposed ADR is created listing payment provider options (e.g., Stripe, Braintree, Adyen) with pros/cons and a recommendation, with the Decision section set to 'Pending human review'. An INT document describes the integration shape (direction, error handling, SLA expectations) but defers provider selection to the ADR. Open items cross-reference the pending decision.",
        files: [],
        product_context_refs: ["Domain Vocabulary"],
      },
      {
        id: 3,
        prompt:
          "Here are notes from our meeting: 'We discussed monitoring. The team agreed we need 99.9% uptime SLA, p99 latency under 200ms for registration API calls, and real-time alerting when error rates spike above 1%. Alice suggested Datadog, Bob preferred CloudWatch since we're already on AWS. No decision was made on tooling.' Write the requirements.",
        expected_output:
          "An NFR document with measurable targets (99.9% uptime, p99 < 200ms, error rate alerting threshold at 1%) — written directly as Draft. An OPS document describing monitoring and alerting requirements (what needs monitoring, alerting rules, incident response) — written directly for the requirements portion. A Proposed ADR or TR for the monitoring tooling decision, listing Datadog and CloudWatch as options with pros/cons, noting each team member's preference, and recommending one with rationale. The ADR/TR has status Proposed with an open item flagging human decision required. The OPS document references the pending tooling decision in its open items.",
        files: [],
        product_context_refs: ["Domain Vocabulary"],
      },
    ],
  },
  null,
  2,
);

/**
 * Reference files emitted alongside the SKILL.md. Keep this list synchronized
 * with the templates exported above; the bundle test asserts the count.
 */
function buildRequirementsWriterReferenceFiles(
  paths: ResolvedAgentPaths,
): ReadonlyArray<{
  readonly path: string;
  readonly content: string;
}> {
  return [
    {
      path: "_references/templates/_template-BR.md",
      content: templateBr(paths),
    },
    {
      path: "_references/templates/_template-FR.md",
      content: templateFr(paths),
    },
    {
      path: "_references/templates/_template-NFR.md",
      content: templateNfr(paths),
    },
    {
      path: "_references/templates/_template-TR.md",
      content: templateTr(paths),
    },
    {
      path: "_references/templates/_template-ADR.md",
      content: templateAdr(paths),
    },
    {
      path: "_references/templates/_template-SEC.md",
      content: templateSec(paths),
    },
    {
      path: "_references/templates/_template-DR.md",
      content: templateDr(paths),
    },
    {
      path: "_references/templates/_template-INT.md",
      content: templateInt(paths),
    },
    {
      path: "_references/templates/_template-OPS.md",
      content: templateOps(paths),
    },
    {
      path: "_references/templates/_template-UX.md",
      content: templateUx(paths),
    },
    {
      path: "_references/templates/_template-MT.md",
      content: templateMt(paths),
    },
    {
      path: "_references/templates/_template-category-README.md",
      content: TEMPLATE_CATEGORY_README,
    },
    {
      path: "_references/templates/_template-requirements-README.md",
      content: templateRequirementsReadme(paths),
    },
    {
      path: "_references/standards-and-frameworks.md",
      content: STANDARDS_AND_FRAMEWORKS,
    },
    {
      path: "evals/evals.json",
      content: WRITE_REQUIREMENT_EVALS_JSON,
    },
  ];
}

/**
 * Sub-agent that authors formal requirement documents using the
 * 11-category taxonomy (BR, FR, NFR, TR, ADR, SEC, DR, INT, OPS, UX, MT)
 * and the decision-authority rules (direct-write vs. propose-only ADR/TR).
 *
 * Picks up `req:write` issues created by the upstream `requirements-analyst`
 * bundle's trace phase, reads the matching template from the skill's
 * reference directory, and writes a single requirement document per
 * session under the configured `<REQUIREMENTS_ROOT>`.
 *
 * This agent writes **requirement documents only** — capability models
 * are the responsibility of the `bcm-writer` agent and gap discovery is
 * the responsibility of the `requirements-analyst` agent.
 */
function buildRequirementsWriterSubAgent(
  paths: ResolvedAgentPaths,
): AgentSubAgent {
  return {
    name: "requirements-writer",
    description:
      "Writes formal requirement documents (BR, FR, NFR, TR, ADR, SEC, DR, INT, OPS, UX, MT) from upstream proposals using the 11-category taxonomy, the four-tier classification, and the decision-authority rules (direct-write vs. propose-only ADR/TR). Handles one req:write issue per session. Produces requirement documents — not capability models or gap reports.",
    model: AGENT_MODEL.POWERFUL,
    maxTurns: 80,
    platforms: { cursor: { exclude: true } },
    prompt: [
      "# Requirements Writer Agent",
      "",
      "You author formal requirement documents using the 11-category",
      "taxonomy (BR, FR, NFR, TR, ADR, SEC, DR, INT, OPS, UX, MT) and the",
      "four-tier architectural classification (Platform, Industry, Customer",
      "Workflow, Consumer Application). Each session handles exactly **one**",
      "`req:write` issue and writes exactly **one** requirement document.",
      "",
      "This agent produces **requirement documents only** — capability",
      "models are written by the `bcm-writer` agent and requirement-gap",
      "discovery is the responsibility of the `requirements-analyst` agent.",
      "Keep this boundary clean: never open `req:scan`,",
      "`req:draft-trace`, or `bcm:*` issues from this pipeline.",
      "",
      "Follow your project's shared agent conventions (`AGENTS.md`,",
      "`CLAUDE.md`, or equivalent) for all commit, branch, and PR rules.",
      "",
      "---",
      "",
      ...PROJECT_CONTEXT_READER_SECTION,
      "## Design Principles",
      "",
      "1. **One requirement per session.** Each `req:write` issue maps to a",
      "   single requirement document. Never write two documents in one",
      "   session and never start a second issue.",
      "2. **Templates are authoritative.** Every category has a template",
      "   under `<TEMPLATES_ROOT>`. Use it verbatim — do not invent new",
      "   sections or reorder existing ones. A section that does not apply",
      "   gets `Not applicable — <reason>` rather than being omitted.",
      "3. **Decision authority is non-negotiable.** Direct-write categories",
      "   ship as `Status: Draft`. ADR and TR documents ship as",
      "   `Status: Proposed` with a Recommendation that frames a human",
      "   decision — never decide for the human.",
      "4. **Trace upstream.** Every requirement links back to the proposal",
      "   that produced it, the source document(s) cited in that proposal,",
      "   and the upstream BCM capability or business need it serves.",
      "5. **Cite, don't invent.** When the proposal does not supply a",
      "   stakeholder, metric, threat model entry, or technology option,",
      "   write `TODO:` and flag the issue with `status:needs-attention`",
      "   rather than fabricating content.",
      "",
      "---",
      "",
      "## Configurable Paths",
      "",
      "The pipeline uses these placeholders. Consuming projects override the",
      "defaults by passing paths in the `/write-requirement` skill",
      `invocation, by recording overrides in \`${paths.docsRoot}/project-context.md\`, or`,
      "by extending this rule in their own `agentConfig.rules`.",
      "",
      "| Placeholder | Meaning | Default |",
      "|-------------|---------|---------|",
      `| \`<REQUIREMENTS_ROOT>\` | Root folder for final requirement documents | \`${paths.requirementsRoot}/\` |`,
      `| \`<RESEARCH_REQUIREMENTS_ROOT>\` | Where the upstream \`requirements-analyst\` writes proposals | \`${paths.researchRequirementsRoot}/\` |`,
      `| \`<TEMPLATES_ROOT>\` | Where the category templates ship (set automatically by this bundle) | \`${REQUIREMENTS_WRITER_PATHS.templatesRoot}\` |`,
      `| \`<STANDARDS_REF>\` | Standards & frameworks reference document | \`${REQUIREMENTS_WRITER_PATHS.standardsRef}\` |`,
      `| \`<PREFIX>\` | Project-specific requirement ID prefix | none — requirement IDs use the category prefix (\`BR\`, \`FR\`, …) unless \`${paths.docsRoot}/project-context.md\` defines a project prefix |`,
      "",
      `If \`${paths.docsRoot}/project-context.md\` specifies a different requirements tree,`,
      "prefer that. Otherwise fall back to the defaults above.",
      "",
      "---",
      "",
      ...REQUIREMENTS_TAXONOMY_TABLE_SECTION,
      "",
      "### Disambiguation Rules",
      "",
      "Apply these rules when deciding where a requirement belongs:",
      "",
      ...REQUIREMENTS_TAXONOMY_DISAMBIGUATION_SECTION,
      "",
      "---",
      "",
      "## Architectural Tiers",
      "",
      "Every requirement also belongs to one of four architectural tiers.",
      'Tier is **orthogonal** to category — category answers "what kind"',
      'while tier answers "where in the architecture." The tier names below',
      "are the bundle defaults; consuming projects may rename or reduce the",
      `tier set in their own \`${paths.docsRoot}/project-context.md\`.`,
      "",
      ...REQUIREMENTS_TIER_TABLE_SECTION,
      "",
      "Tier is a **required metadata field**. Set it in the Metadata table",
      "of every requirement document and apply the matching `tier:*` issue",
      "label (`tier:platform`, `tier:industry`, `tier:customer-workflow`,",
      "or `tier:consumer-app`).",
      "",
      "**Customer field (optional).** When the proposal originated from or",
      "applies to a specific customer, set the `Customer` field in the",
      "Metadata table. Optional for Platform/Industry tiers, expected for",
      "Customer Workflow / Consumer Application tiers when the project",
      "tracks customer profiles.",
      "",
      "**Implementor field (tier-specific).** For Customer Workflow and",
      "Consumer Application tiers, set the `Implementor` field (Consortium",
      "Member / Customer / TBD) when the project tracks that distinction.",
      "Skip the field entirely if it does not apply to your project.",
      "",
      "**Cross-tier traceability.** Higher-tier requirements must link to",
      "lower-tier requirements they depend on using `Depends on (cross-tier)`",
      "links in the Traceability section. For example, a Consumer",
      "Application FR for an intake portal must trace to the Platform INT",
      "for the API it consumes.",
      "",
      "---",
      "",
      "## Decision Authority Rules",
      "",
      "**This is the most important section.** Not all categories are",
      "treated equally. The core principle is: **requirements that describe",
      "*what* and *why* get written directly; decisions about *how* and",
      "*with what* are deferred to humans.**",
      "",
      "### Write Directly (status: `Draft`)",
      "",
      "These categories describe business needs, user behavior, quality",
      "targets, security posture, and user experience. The writer has full",
      "authority to produce these documents:",
      "",
      "- **BR** — Business Requirements",
      "- **FR** — Functional Requirements",
      "- **NFR** — Non-Functional Requirements",
      "- **SEC** — Security & Compliance",
      "- **UX** — UX Requirements",
      "",
      "### Write with Partial Deferral",
      "",
      "These categories contain a mix of *what* (write directly) and *how*",
      "(defer). Write the requirement portions directly but defer",
      "technology/tooling selections:",
      "",
      "- **DR** — Write data models, retention policies, and classification",
      "  directly. When backup strategy or storage technology must be",
      "  specified, create a `Proposed` ADR or TR instead of choosing.",
      "- **MT** — Write tenant isolation requirements and metering",
      "  dimensions directly. When the isolation *model* must be chosen",
      "  (row-level vs schema-per-tenant vs database-per-tenant), create a",
      "  `Proposed` ADR instead of choosing.",
      "- **INT** — Write the integration shape, direction, error handling",
      "  strategy, and SLA expectations directly. When the specific",
      "  provider must be selected, create a `Proposed` TR/ADR instead.",
      "- **OPS** — Write operational requirements (monitoring needs,",
      "  incident response, deployment strategy) directly. When specific",
      "  tooling must be selected, create a `Proposed` TR/ADR instead.",
      "",
      "### Propose Only (status: `Proposed`, pending human decision)",
      "",
      "These categories represent technology and architecture decisions",
      "that humans must make. Produce complete documents with all the",
      "information needed for the decision, but **do not make the decision",
      "itself**.",
      "",
      "- **ADR** — Write the full document following the ADR template:",
      "  1. **Context** — forces at play, constraints, what prompted the",
      "     decision",
      '  2. **Decision** — set to: *"Pending human review. See',
      '     Recommendation below."*',
      "  3. **Alternatives Considered** — every viable option with",
      "     equal-depth analysis. Each gets a description, pros, and cons.",
      "     Do not shortchange options you don't prefer.",
      "  4. **Recommendation** — state which option you recommend, *why*",
      "     it's the best fit for the context, and what trade-offs are",
      "     accepted. The reasoning must be specific to this project's",
      "     situation — not generic.",
      "  5. **Consequences** — positive, negative, and neutral consequences",
      "     of the recommended option",
      "  6. Set status to `Proposed`. Add an open item flagging that a",
      "     human decision is required before dependent requirements can",
      "     proceed.",
      "",
      "- **TR** — Write the full document following the TR template:",
      '  1. **Technology Choice** — set to: *"Pending human review. See',
      '     Recommendation below."*',
      "  2. **Alternatives Considered** — every viable technology option",
      "     with description, pros, cons, license, and maturity assessment",
      "     for each",
      "  3. **Recommendation** — state which technology you recommend,",
      "     *why*, and what trade-offs are accepted",
      '  4. **Rationale** — set to: *"See Recommendation above. Awaiting',
      '     human decision."*',
      "  5. Set status to `Proposed`. Add an open item flagging that a",
      "     human decision is required.",
      "",
      "### How Deferral Works in Practice",
      "",
      "When writing a requirement that implies a technology choice:",
      "",
      "1. Write the requirement itself (FR, DR, INT, OPS, etc.) with full",
      "   detail.",
      "2. Where the requirement needs a technology decision, add a note:",
      `   *"Technology selection pending — see [ADR-NNN](../${paths.requirementCategoryDirs.architecturalDecisions}/ADR-NNN-slug.md)"*.`,
      "3. Create the corresponding ADR or TR as `Proposed` with options,",
      "   pros/cons, and recommendation.",
      '4. In the ADR/TR open items, add: *"Human decision required.',
      '   Dependent requirements: [list]"*.',
      "5. In the original requirement's open items, add a cross-reference:",
      `   *"Blocked by [ADR-NNN](../${paths.requirementCategoryDirs.architecturalDecisions}/ADR-NNN-slug.md) —`,
      '   technology selection pending human review."*',
      "",
      "This creates a clear chain: the requirement is understood, the",
      "decision is framed with all necessary information, and the",
      "dependency is tracked in both directions.",
      "",
      "---",
      "",
      "## File Naming Convention",
      "",
      "Every requirement file follows this pattern:",
      "",
      "```",
      "{PREFIX}-{NNN}-{slug}.md",
      "```",
      "",
      "- **PREFIX** — category abbreviation (`BR`, `FR`, `NFR`, `TR`,",
      "  `ADR`, `SEC`, `DR`, `INT`, `OPS`, `UX`, `MT`). If the project",
      `  declares a project-wide prefix in \`${paths.docsRoot}/project-context.md\`, use`,
      "  it instead.",
      "- **NNN** — sequential number, zero-padded to at least three",
      "  digits (001, 002, 012; grows past 999 naturally, e.g. 1240). The",
      "  ID is **finalized at write-start** via",
      "  `.claude/procedures/next-requirement-id.sh`, which allocates from",
      "  live state (existing docs plus open `req:write` issues). Scanning",
      "  the target category directory under `<REQUIREMENTS_ROOT>` for the",
      "  next available number is the **fallback** used only when that",
      "  procedure is unavailable.",
      "- **slug** — lowercase kebab-case descriptive name.",
      "",
      "Examples: `FR-001-user-registration.md`,",
      "`ADR-003-database-selection.md`,",
      "`SEC-001-authentication-framework.md`.",
      "",
      "---",
      "",
      "## Frontmatter",
      "",
      "Every `.md` requirement file must begin with a YAML frontmatter",
      "block. The minimum required fields are `title` and `description`:",
      "",
      "```markdown",
      "---",
      'title: "FR-001: User Registration"',
      'description: "Functional requirement for the user registration workflow."',
      "tier: platform",
      "---",
      "",
      "...",
      "```",
      "",
      "Titles containing colons must be wrapped in double quotes. The",
      "`tier` field must be one of `platform`, `industry`,",
      "`customer-workflow`, or `consumer-app` (use whatever tier slugs the",
      "project declares).",
      "",
      "### Optional traceability extensions",
      "",
      "Projects that publish requirements through Starlight, Astro, or a",
      "similar static-site generator may add structured traceability fields",
      "to frontmatter. One example: a `referencedIn.meetings[]` block that",
      "links the requirement back to a meeting transcript that informed it.",
      "These conventions are **optional** — adopt them only if your project",
      "already maintains the matching reverse-link structures.",
      "",
      "---",
      "",
      "## Status Lifecycle",
      "",
      "Every requirement has a status that tracks where it is in its",
      "lifecycle:",
      "",
      "| Status | Meaning |",
      "|---|---|",
      "| `Draft` | Initial capture, under discussion |",
      "| `Proposed` | Formally proposed, awaiting review — **used for ADR and TR documents pending human decision** |",
      "| `Accepted` | Approved and active |",
      "| `Implemented` | Fully delivered |",
      "| `Deprecated` | No longer applicable |",
      "| `Superseded` | Replaced by another requirement (link to successor) |",
      "",
      "---",
      "",
      "## Traceability",
      "",
      "Every requirement document must include a `## Traceability` section.",
      "Use relative markdown links with the requirement ID as link text.",
      "",
      "```markdown",
      "## Traceability",
      "",
      `- **Implements:** [BR-001](../${paths.requirementCategoryDirs.business}/BR-001-self-service-onboarding.md)`,
      `- **Constrained by:** [NFR-001](../${paths.requirementCategoryDirs.nonFunctional}/NFR-001-api-response-times.md)`,
      `- **Related:** [SEC-001](../${paths.requirementCategoryDirs.security}/SEC-001-authentication-framework.md)`,
      "```",
      "",
      "### Link Types",
      "",
      "| Link Type | Meaning |",
      "|---|---|",
      "| **Implements** | This requirement realizes a higher-level requirement |",
      "| **Constrained by** | This requirement is bounded by another requirement |",
      "| **Supports** | This requirement contributes to but does not fully realize another |",
      "| **Supersedes** | This requirement replaces a deprecated one |",
      "| **Related** | Informational relationship |",
      "",
      "### Dependency Flow",
      "",
      "```",
      "Business Requirements (BR)",
      "    -> justify epics and initiatives",
      "",
      "Functional (FR), Integration (INT), Multi-Tenancy (MT)",
      "    -> decompose into feature work",
      "",
      "NFRs, Security (SEC), UX",
      "    -> appear as acceptance criteria AND as standalone requirements",
      "",
      "Architectural Decisions (ADR)",
      "    -> spawn implementation tasks and serve as context for TRs",
      "",
      "Technical (TR), Data (DR), Operational (OPS)",
      "    -> drive infrastructure and platform work",
      "```",
      "",
      "---",
      "",
      "## Open Items",
      "",
      "Every requirement document must end with a `## Open Items` section",
      "(placed just above `## Revision History`). This section surfaces",
      "things that need human attention.",
      "",
      "### Priority Levels",
      "",
      "| Priority | Meaning |",
      "|---|---|",
      "| **P0 — Blocker** | Cannot proceed with implementation until resolved |",
      "| **P1 — High** | Significantly affects scope, security, or architecture |",
      "| **P2 — Medium** | Affects quality or completeness but doesn't block progress |",
      "| **P3 — Low** | Minor clarification, can be resolved asynchronously |",
      "",
      "### Subsection Types",
      "",
      "- **Identified Gaps** — Missing functionality, unhandled edge cases,",
      "  incomplete aspects",
      "- **Follow-up Questions** — Ambiguities where intent cannot be",
      "  confidently inferred",
      "- **Contradictions & Inconsistencies** — Conflicts between",
      "  requirements, documentation, or stated goals",
      "",
      "Number each item within its subsection. Assign a priority. Call out",
      "interdependencies with open items in other documents using the",
      `format: \`-> Depends on: [FR-001 Open Item #2](../${paths.requirementCategoryDirs.functional}/FR-001-slug.md#open-items)\`.`,
      "",
      "The `## Open Items` section is **not optional** — it appears in every",
      'document, even if a subsection is empty (write "None identified.").',
      "",
      "---",
      "",
      "## Reading the Templates and Standards Reference",
      "",
      "Before writing any requirement document:",
      "",
      "1. **Read the matching template** under `<TEMPLATES_ROOT>` for the",
      "   category specified in the issue (`_template-FR.md`,",
      "   `_template-ADR.md`, etc.). Templates are named",
      "   `_template-{PREFIX}.md`. Every section in the template must",
      "   appear in the final document.",
      "",
      "2. **Read `<STANDARDS_REF>`** if the category requires it (especially",
      "   SEC, NFR, INT, ADR, TR). The reference covers OWASP ASVS, ISO",
      "   25010, BABOK, MADR, Enterprise Integration Patterns, WCAG, and",
      "   related standards that ground the templates.",
      "",
      "Templates and the standards reference ship with this skill — they",
      "are the same files for every project that adopts the bundle.",
      "",
      "---",
      "",
      "## Maintaining the Registry Index",
      "",
      "Each category directory contains a `README.md` (or `_index.md`,",
      "depending on project convention) that lists every requirement in",
      "that category. **After writing any new requirement document, update",
      "the category index:**",
      "",
      "1. Read the index file in the same directory as the new requirement.",
      "2. Add a row to the requirements table with: ID (linked to the",
      "   file), Title, Status, Priority, Last Updated date.",
      "3. Insert the row in sequence-number order.",
      "4. If the index contains a placeholder message, remove it.",
      "",
      "If the project has not seeded category READMEs yet, generate one",
      "from `_template-category-README.md` (shipped with this skill) and",
      "commit it alongside the requirement document.",
      "",
      "---",
      "",
      "## Agent Loop",
      "",
      "Run this loop exactly once per session. Never start a second issue.",
      "",
      "1. Claim one open `req:write` issue (also carries `type:requirement`",
      "   and a `tier:*` label).",
      "2. Transition `status:ready` → `status:in-progress` and create the",
      "   branch per your project's branch-naming convention.",
      "3. Execute the write phase below.",
      "4. Commit, push, open a PR, and close the issue per your project's",
      "   PR workflow. Closing the PR transitions the issue to",
      "   `status:done` per the standard label conventions.",
      "   Stamp every PR you open with the `origin:agent` label — see the",
      "   **Origin label** section below.",
      "",
      "---",
      "",
      ...renderClaimVerificationSection(),
      "",
      "---",
      "",
      ...renderOriginStampSection(),
      "",
      "---",
      "",
      "## The `req:write` Issue Schema",
      "",
      "This section is the **authoritative schema** for `req:write` issue",
      "bodies. Upstream producers (the `requirements-analyst` trace phase,",
      "the `requirements-reviewer` follow-up generator, and any human",
      "authoring a `req:write` issue directly) must embed or link to this",
      "same schema so the three required fields are always present when",
      "the writer picks the issue up.",
      "",
      ...REQ_WRITE_ISSUE_SCHEMA_SECTION,
      "",
      "---",
      "",
      "## The `req:write` Phase",
      "",
      "**Goal:** Read the proposal that produced this issue, write a single",
      "requirement document under `<REQUIREMENTS_ROOT>`, and update the",
      "category index.",
      "",
      "**Budget:** Read the issue body, the named proposal file under",
      "`<RESEARCH_REQUIREMENTS_ROOT>`, the matching category template under",
      "`<TEMPLATES_ROOT>`, and `<STANDARDS_REF>` if the category needs it.",
      "Write one requirement document and one category-index update. No web",
      "searches.",
      "",
      "### Steps",
      "",
      "1. **Parse the issue.** The `req:write` issue body follows the",
      "   authoritative schema in the",
      "   [The `req:write` Issue Schema](#the-reqwrite-issue-schema)",
      "   section below. Before writing, verify every required field",
      "   (Category, Tier, Output Path) is present and internally",
      "   consistent:",
      "",
      "   - Category matches the prefix on the requirement title and on",
      "     the Output Path filename.",
      "   - Tier matches the `tier:*` label applied to the issue.",
      "   - Output Path sits under",
      "     `<REQUIREMENTS_ROOT>/<category-dir>/<PREFIX>-<NNN>-<slug>.md`.",
      "",
      "   If any required field is missing, contradictory, or the issue",
      "   already carries `status:needs-attention` with a `Missing:` line",
      "   (upstream could not derive it), comment on the issue with",
      "   which field is wrong, ensure `status:needs-attention` is set,",
      "   and stop without writing. A human triages before the writer",
      "   tries again.",
      "",
      "2. **Read the proposal.** Open the proposals file referenced in the",
      "   issue inputs (under `<RESEARCH_REQUIREMENTS_ROOT>`). Find the",
      "   specific proposal entry that matches this requirement's title and",
      "   category. Treat the proposal as the authoritative source — do not",
      "   invent fields it omits.",
      "",
      "3. **Read the matching template** under `<TEMPLATES_ROOT>` for the",
      "   category. Read `<STANDARDS_REF>` if the category is SEC, NFR,",
      "   INT, ADR, or TR — these depend on the standards reference for",
      "   correct framing.",
      "",
      "4. **Finalize the requirement ID first — before you author any",
      "   document content, filename, or self-reference.** Run",
      "   `.claude/procedures/next-requirement-id.sh <PREFIX> <category-dir> <this-issue-number>`",
      "   to compute the next free ID from live state (existing docs plus",
      "   open `req:write` issues, with the issue-number tiebreak). Use",
      "   `<category-dir>` = the resolved `<REQUIREMENTS_ROOT>/<category-dir>`",
      "   for the target category, and `<this-issue-number>` = the number of",
      "   the `req:write` issue you are working.",
      "",
      "   - If the issue's reserved `Requirement ID` hint is present **and",
      "     equals** the procedure's output, use it.",
      "   - Otherwise use the **procedure's output** — a concurrent claim",
      "     took the reserved number, and the procedure has already resolved",
      "     the collision in your favor via the issue-number order.",
      "",
      "   **Author the document exactly once with this finalized ID** — the",
      "   filename and every in-body self-reference. **Never renumber after",
      "   authoring:** the ID is fixed before any body content exists, so a",
      "   concurrent claim is resolved here for free — no rename, no",
      "   self-reference rewrite, no post-authoring renumber treadmill.",
      "",
      "   After finalizing, and before opening the PR, you SHOULD update this",
      "   issue's `Requirement ID` field to the finalized value",
      "   (`gh issue edit <this-issue-number>`) so other in-flight writers",
      "   see the resolved claim. This is best-effort — do not block or fail",
      "   the write if the edit does not succeed.",
      "",
      "   If `next-requirement-id.sh` is unavailable, fall back to scanning",
      "   the target category directory under `<REQUIREMENTS_ROOT>` for",
      "   existing files matching `{PREFIX}-NNN-*.md` and take the next",
      "   unused three-digit number.",
      "",
      "5. **Apply the decision-authority rules.** Default to `Status:",
      "   Draft` for direct-write categories (BR, FR, NFR, SEC, UX). Set",
      "   `Status: Proposed` for ADR and TR documents and frame the",
      "   Recommendation section as a human decision. For DR/MT/INT/OPS",
      "   categories that imply a technology choice, write the requirement",
      "   directly but spin the technology decision off into a separate",
      "   `Proposed` ADR or TR — record the cross-link in the Open Items",
      "   section of both documents.",
      "",
      "6. **Write the document.** Fill every section in the template. For",
      "   sections the proposal does not supply, write `TODO:` plus a brief",
      "   note describing what input is needed, and add a corresponding",
      "   `## Open Items` entry. Never leave a template section out.",
      "",
      "7. **Set frontmatter.** At minimum: `title`, `description`, `tier`.",
      "   If the project declares optional frontmatter conventions in",
      `   \`${paths.docsRoot}/project-context.md\` (such as \`referencedIn.meetings[]\`),`,
      "   honor them. Otherwise stop at the minimum.",
      "",
      "8. **Update the category index.** If the category directory has a",
      "   `README.md` or `_index.md` registry, add a row for the new",
      "   document in sequence order. If no index exists yet, generate one",
      "   from `_template-category-README.md`. The requirement ID was",
      "   already finalized at write-start (step 4), so this is a plain",
      "   append-only, deterministic row insert — never renumber existing",
      "   rows or mutate a sequence counter here.",
      "",
      "9. **Cross-link upstream.** Add `## Traceability` entries pointing",
      "   to the proposals file (under `<RESEARCH_REQUIREMENTS_ROOT>`),",
      "   the source documents the proposal cited, and any BCM capability",
      "   the requirement supports.",
      "",
      "10. **Quality checks.** Before committing, verify:",
      "    - [ ] Frontmatter has `title` and `description` (and `tier` if",
      "          the project uses tier classification)",
      "    - [ ] Title matches the `# Heading` line",
      "    - [ ] File name follows `{PREFIX}-{NNN}-{slug}.md`",
      "    - [ ] Status is `Draft` for direct-write categories or `Proposed`",
      "          for ADR/TR",
      "    - [ ] Every template section is present (use `TODO:` or `Not",
      "          applicable — <reason>` for unfilled sections)",
      "    - [ ] `## Traceability` exists with at least one upstream link",
      "    - [ ] No technology decisions made in direct-write categories —",
      "          deferred to `Proposed` ADR/TR with cross-links in Open",
      "          Items",
      "    - [ ] `## Open Items` is present with Identified Gaps,",
      "          Follow-up Questions, and Contradictions subsections",
      "    - [ ] ADR/TR documents include a Recommendation section and an",
      "          Open Item flagging the human decision required",
      "    - [ ] Category index updated with a row for the new document",
      "    - [ ] Tier value matches the issue's `tier:*` label",
      "    - [ ] Cross-tier traceability entries exist where the document",
      "          depends on or enables a different tier",
      "",
      "11. **Commit and push.** Use a `docs(<category>):` conventional",
      "    commit message. The PR closes the `req:write` issue.",
      "",
      "---",
      "",
      "## Output Boundaries",
      "",
      "This agent writes **only** to:",
      "",
      "- `<REQUIREMENTS_ROOT>/<category-dir>/<PREFIX>-<NNN>-<slug>.md` — one",
      "  requirement document per session",
      "- `<REQUIREMENTS_ROOT>/<category-dir>/README.md` (or `_index.md`) —",
      "  category index, one row appended per session",
      "- `<REQUIREMENTS_ROOT>/README.md` (or `_index.md`) — only if the",
      "  top-level requirements README does not yet exist; generate from",
      "  `_template-requirements-README.md` and stop",
      "",
      "The pipeline produces **requirement documents**. It does not write",
      "BCM capability models, people profiles, company profiles, software",
      "profiles, scan reports, or proposal files — those belong to",
      "specialized upstream/downstream agents.",
      "",
      "**Do NOT create:**",
      "- `req:scan` or `req:draft-trace` issues — those belong to",
      "  the `requirements-analyst` bundle",
      "- `bcm:*` issues — those belong to the `bcm-writer` bundle",
      "- `people:*`, `company:*`, `software:*`, `research:*`, or",
      "  `industry:*` issues — those belong to their respective bundles",
      "",
      "If the proposal surfaces work that needs one of the above, comment",
      "on the `req:write` issue with the suggested follow-up and let a",
      "human route it.",
      "",
      "---",
      "",
      "## Coordination with Other Agents",
      "",
      "| Direction | Agent | What |",
      "|-----------|-------|------|",
      "| Upstream | `requirements-analyst` | Discovers gaps, drafts proposals, and creates `req:write` issues that this agent picks up |",
      "| Upstream | `bcm-writer` | Provides BCM capability documents that requirements trace back to via `## Traceability` links |",
      "| Peer | `meeting-analyst` | Provides meeting transcripts that may inform a requirement's traceability extensions (optional) |",
      "",
      "**File boundaries:** Reads `<RESEARCH_REQUIREMENTS_ROOT>` (proposals)",
      "and the source documents the proposals cite. Writes",
      "`<REQUIREMENTS_ROOT>` and the category index files. Never edits",
      "proposals, scan reports, BCM documents, or profiles.",
      "",
      "---",
      "",
      "## Rules",
      "",
      "- **One requirement per session.** Never write two documents in one",
      "  session and never start a second issue.",
      "- **Templates are authoritative.** Use the shipped template verbatim",
      "  for the category. Every template section must appear in the final",
      "  document.",
      "- **Decision authority is non-negotiable.** Direct-write categories",
      "  ship as `Draft`. ADR and TR ship as `Proposed` with a Recommendation",
      "  framed for human decision. Mixed-deferral categories spin",
      "  technology choices off into separate `Proposed` documents.",
      "- **Cite, don't invent.** When the proposal omits a stakeholder,",
      "  metric, threat model entry, or technology option, write `TODO:` and",
      "  flag the issue with `status:needs-attention`.",
      "- **Trace upstream.** Every requirement links back to its proposal,",
      "  the source documents the proposal cited, and the BCM capability",
      "  it supports (when applicable).",
      "- **Update the category index every time.** A requirement that",
      "  exists as a file but is missing from its category index is",
      "  invisible to anyone browsing the documentation tree.",
      "- **Write requirements, not capability models or gap reports.**",
      "  Never open `req:scan`, `req:draft-trace`, or `bcm:*` issues from",
      "  this pipeline.",
    ].join("\n"),
  };
}

/** Skill that kicks off a requirement-writing session. */
function buildWriteRequirementSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: WRITE_REQUIREMENT_SKILL_NAME,
    description:
      "Write one formal requirement document (BR / FR / NFR / TR / ADR / SEC / DR / INT / OPS / UX / MT) using the shipped category template and decision-authority rules. Picks up a req:write issue created by the upstream requirements-analyst pipeline (or kicked off ad hoc) and dispatches the requirements-writer agent.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "requirements-writer",
    platforms: { cursor: { exclude: true } },
    referenceFiles: buildRequirementsWriterReferenceFiles(paths),
    instructions: [
      "# Write Requirement",
      "",
      "Write one formal requirement document using the 11-category taxonomy",
      "(BR, FR, NFR, TR, ADR, SEC, DR, INT, OPS, UX, MT) and the",
      "decision-authority rules (direct-write vs. propose-only ADR/TR).",
      "Dispatches the `requirements-writer` agent.",
      "",
      "## Usage",
      "",
      "/write-requirement <category> <short-title>",
      "",
      "Where `<category>` is one of `BR`, `FR`, `NFR`, `TR`, `ADR`, `SEC`,",
      "`DR`, `INT`, `OPS`, `UX`, `MT`.",
      "",
      "Optional extensions in the issue body:",
      "- `tier: platform | industry | customer-workflow | consumer-app` —",
      "  the architectural tier (default: `platform`)",
      "- `prefix: <PROJECT_PREFIX>` — override the default category prefix",
      `  with a project-specific one declared in \`${paths.docsRoot}/project-context.md\``,
      "- `customer: <link-or-slug>` — link the requirement to a customer",
      "  profile (expected for Customer Workflow / Consumer Application",
      "  tiers in projects that track customer profiles)",
      "- `proposal: <path>` — pin the upstream proposal file under",
      "  `<RESEARCH_REQUIREMENTS_ROOT>` (default: derived from the issue",
      "  context)",
      "- `output: <path>` — override the default Output Path",
      "",
      "## Default Paths",
      "",
      `If the project has no override in \`${paths.docsRoot}/project-context.md\` or`,
      "`agentConfig.rules`, outputs land under:",
      "",
      `- \`${paths.requirementsRoot}/<category-dir>/<PREFIX>-<NNN>-<slug>.md\``,
      `- \`${paths.requirementsRoot}/<category-dir>/README.md\` (registry update)`,
      `- \`${paths.requirementsRoot}/README.md\` (top-level README, generated on`,
      "  first use only)",
      "",
      "Templates and the standards reference ship with this skill under",
      "`_references/templates/` and `_references/standards-and-frameworks.md`.",
      "",
      "## Steps",
      "",
      "1. Create a `req:write` issue with `type:requirement`,",
      `   \`priority:${labelsForPhase(issueDefaults, "req:write").priority}\`, \`status:${labelsForPhase(issueDefaults, "req:write").status}\`, and the matching \`tier:*\` label.`,
      "   Body must include the category, tier, output path, and a pointer",
      "   to the upstream proposal (or a direct user description if no",
      "   proposals file exists).",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute the write phase of the requirements-writer agent.",
      "3. The agent writes one requirement document, updates the category",
      "   index, opens a PR, and closes the issue.",
      "",
      "## Output",
      "",
      "- One requirement document under `<REQUIREMENTS_ROOT>` following the",
      "  shipped category template, with `Status: Draft` for direct-write",
      "  categories or `Status: Proposed` for ADR/TR",
      "- A category-index row pointing at the new document",
      "- (First-time only) a top-level requirements README derived from",
      "  `_template-requirements-README.md`",
    ].join("\n"),
  };
}

/**
 * Render the shell body of the
 * `.claude/procedures/next-requirement-id.sh` helper. Exported so the
 * requirements-writer bundle (and, by re-export, the analyst bundle)
 * can register it as an `AgentProcedure` and the bundles test suite
 * can assert on the script's contents.
 *
 * Contract:
 *
 * ```
 * next-requirement-id.sh <PREFIX> <category-dir> [<self-issue-number>]
 * ```
 *
 * Prints the next free requirement ID (e.g. `FR-1240`) to stdout and
 * exits `0`. The number is the max of two candidate sources plus one:
 *
 *   1. **Merged docs** — files in `<category-dir>` whose names match
 *      `^<PREFIX>-([0-9]+)-.*\.md$`.
 *   2. **Live state** — open `req:write` GitHub issues whose body
 *      carries a `Requirement ID: <PREFIX>-<NNN>` field. When
 *      `<self-issue-number>` is supplied, only issues **strictly
 *      lower-numbered** than it are counted; this excludes the caller's
 *      own reservation and imposes an issue-number total order so two
 *      concurrent writers never both pick the same number (the
 *      lower-numbered issue ignores the higher; the higher accounts for
 *      the lower). Omit the argument (analyst use) to count every open
 *      `req:write` issue.
 *
 * Every `gh`/`jq` call is guarded so a missing binary, missing auth, or
 * a non-repo cwd contributes nothing — the helper degrades to the
 * directory scan and still prints a valid ID. Side-effect-free; never
 * mutates issues, docs, or any counter.
 *
 * A separate open-PR query is intentionally omitted: a `req:write`
 * issue stays OPEN until its PR MERGES (`Closes #N` closes on merge),
 * so open issues already subsume in-flight PRs — provided each writer
 * publishes its finalized ID back to the issue field per the write-start
 * step.
 */
export function renderNextRequirementIdProcedure(): string {
  return [
    "#!/usr/bin/env bash",
    "# next-requirement-id.sh — print the next free requirement ID for a",
    "# category, allocated from live GitHub state (open req:write issues)",
    "# plus the merged docs already on disk.",
    "#",
    "# Usage:",
    "#   .claude/procedures/next-requirement-id.sh <PREFIX> <category-dir> [<self-issue-number>]",
    "#",
    "# Example:",
    "#   next-requirement-id.sh FR docs/src/content/docs/requirements/functional 850",
    "#   -> FR-1240",
    "#",
    "# Behaviour:",
    "#   - Collects candidate NNN numbers from (1) files in <category-dir>",
    "#     matching ^<PREFIX>-([0-9]+)-.*\\.md$ and (2) open req:write",
    "#     issues whose body carries a `Requirement ID: <PREFIX>-<NNN>`",
    "#     line. Prints `<PREFIX>-<max+1>` zero-padded to at least three",
    "#     digits (grows past 999 naturally, e.g. FR-1240).",
    "#   - When <self-issue-number> is given, only open req:write issues",
    "#     with a STRICTLY LOWER number are counted. This excludes the",
    "#     caller's own reservation and imposes an issue-number total",
    "#     order so two concurrent writers never both pick the same",
    "#     number. Omit it (analyst use, before any issue is filed) to",
    "#     count every open req:write issue.",
    "#",
    "# Guards:",
    "#   - Side-effect-free: never edits issues, docs, or any counter.",
    "#   - Every gh/jq call is guarded (|| true, 2>/dev/null); a missing",
    "#     binary, missing auth, or non-repo cwd contributes nothing and",
    "#     the helper falls back to the directory scan.",
    "#   - Prints a usage message to stderr and exits 2 on bad args; a",
    "#     missing or empty <category-dir> is tolerated (scan finds",
    "#     nothing). Happy path always exits 0.",
    "",
    "set -uo pipefail",
    "",
    "usage() {",
    '  printf "usage: next-requirement-id.sh <PREFIX> <category-dir> [<self-issue-number>]\\n" >&2',
    "}",
    "",
    "# Require at least a prefix and a category directory argument.",
    'if [ "$#" -lt 2 ]; then',
    "  usage",
    "  exit 2",
    "fi",
    "",
    'prefix="$1"',
    'category_dir="$2"',
    'self_issue="${3:-}"',
    "",
    "# The running maximum NNN seen across both candidate sources. Starts",
    "# at 0 so an empty category with no open issues yields <PREFIX>-001.",
    "max=0",
    "",
    "# Fold a bare integer candidate into the running max. Ignores any",
    "# value that is not a run of digits (defensive against malformed",
    "# filenames or issue bodies).",
    "consider() {",
    '  local n="$1"',
    '  case "$n" in',
    "    '' | *[!0-9]*) return 0 ;;",
    "  esac",
    "  # Strip leading zeros for base-10 arithmetic (avoid octal parsing).",
    "  n=$((10#$n))",
    '  if [ "$n" -gt "$max" ]; then',
    '    max="$n"',
    "  fi",
    "}",
    "",
    "# --- Source 1: merged docs on disk -------------------------------------",
    "# Scan <category-dir> for files named <PREFIX>-<NNN>-<slug>.md and fold",
    "# each NNN into the running max. A missing/empty directory is a no-op.",
    'if [ -n "$category_dir" ] && [ -d "$category_dir" ]; then',
    '  for f in "$category_dir"/"$prefix"-*.md; do',
    "    # Guard the literal-glob case when nothing matches.",
    '    [ -e "$f" ] || continue',
    '    base="$(basename "$f")"',
    "    # Extract the digit run between the prefix and the next hyphen.",
    '    nnn="${base#"$prefix"-}"',
    '    nnn="${nnn%%-*}"',
    '    consider "$nnn"',
    "  done",
    "fi",
    "",
    "# --- Source 2: live state — open req:write issues ----------------------",
    "# Ask GitHub for open req:write issues and parse each body for a",
    "# `Requirement ID: <PREFIX>-<NNN>` field. The whole block is guarded so",
    "# a missing gh/jq, missing auth, or non-repo cwd simply yields no",
    "# candidates and the directory scan stands alone.",
    "if command -v gh >/dev/null 2>&1 && command -v jq >/dev/null 2>&1; then",
    '  issues_json="$(gh issue list --label req:write --state open --limit 500 --json number,body 2>/dev/null || true)"',
    '  if [ -n "$issues_json" ]; then',
    "    # Flatten each issue to a single `<number>\\t<body>` line (newlines",
    "    # in the body collapsed to spaces) so a plain read loop can walk",
    "    # them. jq is guarded; on any parse error `parsed` is empty and the",
    "    # loop runs zero times. Captured into a variable first so the loop",
    "    # runs in THIS shell (a piped `while` would subshell and lose max).",
    '    parsed="$(printf \'%s\' "$issues_json" | jq -r \'.[] | "\\(.number)\\t\\(.body // "" | gsub("[\\\\n\\\\r]"; " "))"\' 2>/dev/null || true)"',
    '    if [ -n "$parsed" ]; then',
    "      while IFS=$'\\t' read -r issue_number issue_body; do",
    '        [ -n "$issue_number" ] || continue',
    "        # Skip non-numeric issue numbers defensively.",
    '        case "$issue_number" in',
    "          '' | *[!0-9]*) continue ;;",
    "        esac",
    "        # Issue-number total order: when a self issue is given, count",
    "        # this issue only if it is STRICTLY lower-numbered than us.",
    '        if [ -n "$self_issue" ]; then',
    '          case "$self_issue" in',
    "            '' | *[!0-9]*) : ;;",
    "            *)",
    '              if [ "$issue_number" -ge "$self_issue" ]; then',
    "                continue",
    "              fi",
    "              ;;",
    "          esac",
    "        fi",
    "        # Pull the NNN out of a `Requirement ID: <PREFIX>-<NNN>` field.",
    "        # Tolerant of surrounding markdown (**bold**, leading `- `) and",
    "        # case-insensitive on the label. First match wins.",
    '        id_match="$(printf \'%s\\n\' "$issue_body" | grep -ioE "Requirement ID:[^A-Za-z0-9]*$prefix-[0-9]+" | head -n1 || true)"',
    '        if [ -n "$id_match" ]; then',
    "          id_nnn=\"$(printf '%s' \"$id_match\" | grep -oE '[0-9]+$' || true)\"",
    '          consider "$id_nnn"',
    "        fi",
    "      done <<EOF",
    "$parsed",
    "EOF",
    "    fi",
    "  fi",
    "fi",
    "",
    "# next = max seen + 1, printed zero-padded to at least three digits.",
    "next=$((max + 1))",
    'printf \'%s-%03d\\n\' "$prefix" "$next"',
    "",
    "exit 0",
  ].join("\n");
}

/**
 * `AgentProcedure` definition for
 * `.claude/procedures/next-requirement-id.sh`. Registered on **both**
 * the requirements-writer and requirements-analyst bundles — both auto-
 * detect (`appliesWhen: () => true`) and `AgentConfig.resolveProcedures`
 * dedupes by `name` into a `Map`, so the script is emitted to disk
 * exactly once even when both bundles are active. The writer invokes it
 * at write-start to finalize the ID before authoring; the analyst
 * invokes it to seed the first reserved ID in a category.
 */
export const nextRequirementIdProcedure: AgentProcedure = {
  name: "next-requirement-id.sh",
  description:
    "Prints the next free requirement ID (e.g. FR-1240) for a category, allocated from live GitHub state (open req:write issues, with an optional strictly-lower issue-number tiebreak) plus the merged docs on disk. Side-effect-free; every gh/jq call is guarded so a missing binary, missing auth, or non-repo cwd degrades to the directory scan. Exits 0 on the happy path.",
  content: renderNextRequirementIdProcedure(),
};

/**
 * Requirements writer bundle — enabled by default for projects that adopt
 * this batch.
 *
 * Consuming projects can disable it with
 * `excludeBundles: ["requirements-writer"]`. `appliesWhen` always returns
 * `true` per this batch's directive that bundles assume peers are
 * present.
 *
 * Ships a single consolidated sub-agent (`requirements-writer`) with the
 * 11-category taxonomy and decision-authority logic in one prompt, a
 * user-invocable skill (`/write-requirement`) that ships all 13 templates
 * plus `standards-and-frameworks.md` as `referenceFiles`, and the
 * `req:write` plus four `tier:*` labels via the `labels` mechanism.
 *
 * `type:requirement` is intentionally **not** declared here — it is
 * already declared by the `requirements-analyst` bundle and reused.
 */
export function buildRequirementsWriterBundle(
  paths: ResolvedAgentPaths = DEFAULT_AGENT_PATHS,
  issueDefaults: ResolvedIssueDefaults = DEFAULT_RESOLVED_ISSUE_DEFAULTS,
): AgentRuleBundle {
  return {
    name: "requirements-writer",
    description:
      "Requirements writer agent bundle. Authors formal requirement documents from upstream proposals using the 11-category taxonomy (BR, FR, NFR, TR, ADR, SEC, DR, INT, OPS, UX, MT), the four-tier classification, and decision-authority rules (direct-write vs. propose-only). Ships 13 templates plus a standards-and-frameworks reference.",
    appliesWhen: () => true,
    rules: [
      {
        name: "requirements-writer-workflow",
        description:
          "Describes the requirements-writer pipeline, the req:write phase label, the four tier:* labels, and the boundary with the upstream requirements-analyst and bcm-writer bundles.",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Requirements Writer Workflow",
          "",
          "Use `/write-requirement <category> <short-title>` to author one",
          "formal requirement document. The writer runs in a single phase",
          "tracked by a GitHub issue labeled `req:write` plus the matching",
          "`tier:*` label. Issues also carry `type:requirement` (declared",
          "by the upstream `requirements-analyst` bundle).",
          "",
          "The pipeline produces **requirement documents only** — capability",
          "models are written by the `bcm-writer` agent and gap discovery is",
          "the responsibility of the `requirements-analyst` agent. The",
          "writer never opens `req:scan`, `req:draft-trace`, or `bcm:*`",
          "issues.",
          "",
          "Documents follow the 11-category taxonomy (BR, FR, NFR, TR, ADR,",
          "SEC, DR, INT, OPS, UX, MT) and the four-tier classification",
          "(Platform, Industry, Customer Workflow, Consumer Application).",
          "Templates and a standards-and-frameworks reference ship with the",
          "skill — they are the same files for every project that adopts",
          "the bundle.",
          "",
          "Decision-authority rules are non-negotiable: BR / FR / NFR /",
          "SEC / UX ship as `Status: Draft`; ADR and TR ship as",
          "`Status: Proposed` with a Recommendation framed for human",
          "decision; DR / MT / INT / OPS spin technology choices off into",
          "separate `Proposed` ADR or TR documents.",
          "",
          "See the `requirements-writer` agent definition for full workflow",
          "details, configurable paths, decision-authority rules, and",
          "phase-by-phase instructions.",
        ].join("\n"),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
    ],
    skills: [buildWriteRequirementSkill(paths, issueDefaults)],
    subAgents: [buildRequirementsWriterSubAgent(paths)],
    procedures: [nextRequirementIdProcedure],
    labels: [
      {
        name: "req:write",
        color: "FEF2C0",
        description:
          "Phase: write a formal requirement document using the requirements-writer skill",
      },
      {
        name: "tier:platform",
        color: "EDEDED",
        description:
          "Architectural tier: core platform (shared infrastructure, APIs, auth, tenant isolation)",
      },
      {
        name: "tier:industry",
        color: "EDEDED",
        description:
          "Architectural tier: industry vertical (capabilities not every tenant needs)",
      },
      {
        name: "tier:customer-workflow",
        color: "EDEDED",
        description:
          "Architectural tier: customer-configured workflow (business logic tenants configure)",
      },
      {
        name: "tier:consumer-app",
        color: "EDEDED",
        description:
          "Architectural tier: consumer application (UI/UX and integrations in external front-ends/systems)",
      },
    ],
  };
}

/**
 * Default-paths instance of the requirements-writer bundle, preserved
 * for backward compatibility with consumers that import the const
 * directly. The factory above is the canonical entry point when a
 * consumer supplies `AgentConfigOptions.paths`.
 */
export const requirementsWriterBundle: AgentRuleBundle =
  buildRequirementsWriterBundle();
